// Entry point for the WEB RADIO page (public/radio.html) - a dedicated copy
// of main.js (party-mode.html's entry) with the Sonos plumbing swapped out:
//
//   - the profile comes from Spotify Soloist (soloist-visual-profile.js,
//     polling /api/soloist/now) instead of the Sonos zones;
//   - the page PLAYS the music itself, from the Icecast stream of Soloist's
//     output (radio-player.js), instead of a speaker playing it in the room;
//   - Left/Right skip Soloist's track (the whole radio), Up/Down adjust this
//     listener's own volume rather than a Sonos zone's.
//
// Everything else - the visualizer library, the orchestrator, artwork palette
// and crossfade, lyrics banner, radiopi analysis, the debug panels - is the
// same code, imported from the same modules, and behaves identically. When
// main.js changes, this file is meant to receive the same change; the diff
// between the two should stay limited to the blocks marked "RADIO:".

import './debug-capture.js';   // FIRST: ?freezetime must replace the clocks before anything reads them
import { API_BASE } from './api-base.js';
import { SceneManager } from './scene-manager.js';
import { cleanDisplayTitle, cleanDisplayArtist } from './title-clean.js';
import { resolveDanceabilityIndex } from './sonos-visual-profile.js';
// RADIO: Soloist state + transport in place of the Sonos zones/transport.
import { createSoloistVisualProfileSource, radioLatencySeconds, RADIO_LATENCE_FIXE_S } from './soloist-visual-profile.js';
import {
  creerBarreTechnique, fmtClock, fmtTimingSegment, currentPositionSeconds,
  lyricsOffsetSeconds, fmtLyricSegment, fmtPhaseSegment, fmtApiStatusSegment,
  fmtMixPointsSegment, fmtLyricsSourcePanel
} from './track-debug.js';
// RADIO: le panneau technique de halofloor.com (paroles, analyse, ligne du morceau).
import { creerPanneauDebug } from './ecran-debug.js';
import { RadioPlayer } from './radio-player.js';
import { ExternalRadio, STATIONS } from './external-radio.js';
import { extractArtworkPalette } from './artwork-palette.js';
import { fetchTrackFeatures } from './track-features-client.js';
import { fetchTrackAnalysis } from './track-analysis-client.js';
import { bpmFromAnalysis, porteDepuisProfil } from './track-analysis.js';
import { fetchLyrics } from './lyrics-client.js';
import { findRepeatedPhrases } from './lyric-hook.js';
import { PartyModeController } from './party-mode-controller.js';
import { preloadCrowdAtlas, getCrowdAtlas } from './crowd-atlas.js';
import { preloadMatrixAtlas } from './matrix-atlas.js';
import { preloadSoundboardTexture, getSoundboardTexture } from './soundboard-texture.js';
import { preloadIconAtlas } from './icon-atlas.js';
import { preloadPaperTexture, getPaperTexture } from './paper-texture.js';
import { preloadFlapFont, getFlapFont } from './flap-font.js';
import { preloadHersheyFont, preloadHersheySans } from './hershey-font.js';
import { preloadRosace } from './rosace-assets.js';
import { preloadAlley } from './alley-assets.js';
import { preloadFairground } from './fairground-assets.js';
import { preloadMoonlake } from './moonlake-assets.js';
import { preloadCloudsea } from './cloudsea-assets.js';
import { preloadThunderhead } from './thunderhead-assets.js';
import { preloadFountain } from './fountain-assets.js';
import { preloadSeasons } from './seasons-assets.js';
import { preloadTvwall } from './tvwall-assets.js';
import { preloadBalloons } from './balloons-assets.js';
import { preloadHourglass } from './hourglass-assets.js';
import { preloadUnderground } from './underground-assets.js';
import { preloadBayfrontSkyline } from './bayfront-skyline-assets.js';
import { preloadBallroom } from './ballroom-assets.js';
import { preloadSkyline } from './skyline-assets.js';
import { preloadArcade } from './arcade-assets.js';
import { CANDIDATES } from './visualizer-registry.js';
import { VISUALIZERS } from './visualizer-table.js';
import { scoreVisualization, disableVisualization, selectDanceabilityPool, libraryForTrack } from './orchestrator.js';
// Step 6: real visualizers, one entry per finished visualizer. rig-test.js
// (steps 4/5's validation harness) stays in the repo but is no longer
// wired in here. geometry.js (static truss) was judged "not impressive"
// and pulled from the active rotation - kept in the repo for a later
// static/artwork-driven visualizer, not wired in below.
import { NeonPulseVisualizer } from './visualizers/neon-pulse.js';
import { ParticleStormVisualizer } from './visualizers/particle-storm.js';
import { PulseCoreVisualizer } from './visualizers/pulse-core.js';
import { KineticRigVisualizer } from './visualizers/kinetic-rig.js';
import { LaserCathedralVisualizer } from './visualizers/laser-cathedral.js';
import { LaserGridVisualizer } from './visualizers/laser-grid.js';
import { MainStageVisualizer } from './visualizers/main-stage.js';
import { KaleidoDriftVisualizer } from './visualizers/kaleido-drift.js';
import { TypeSlamVisualizer } from './visualizers/type-slam.js';
import { VinylGrooveVisualizer } from './visualizers/vinyl-groove.js';
import { SpotWallVisualizer } from './visualizers/spot-wall.js';
import { NeonGridVisualizer } from './visualizers/neon-grid.js';
import { DaftPyramidVisualizer } from './visualizers/daft-pyramid.js';
import { AdaptivePartyVisualizer } from './visualizers/adaptive-party.js';
import { DiscoSquaresVisualizer } from './visualizers/disco-squares.js';
import { NeonParticlesVisualizer } from './visualizers/neon-particles.js';
import { ChromeFluxVisualizer } from './visualizers/chrome-flux.js';
import { PhosphorScopeVisualizer } from './visualizers/phosphor-scope.js';
import { ConstellationVisualizer } from './visualizers/constellation.js';
import { DrosteDiveVisualizer } from './visualizers/droste-dive.js';
import { ResonanceVisualizer } from './visualizers/resonance.js';
import { HalftoneVisualizer } from './visualizers/halftone.js';
import { SumiVisualizer } from './visualizers/sumi.js';
import { LoomVisualizer } from './visualizers/loom.js';
import { SplitflapVisualizer } from './visualizers/splitflap.js';
import { CausticsVisualizer } from './visualizers/caustics.js';
import { AuroraVisualizer } from './visualizers/aurora.js';
import { FrostVisualizer } from './visualizers/frost.js';
import { ReedVisualizer } from './visualizers/reed.js';
import { AnamorphVisualizer } from './visualizers/anamorph.js';
import { PortalVisualizer } from './visualizers/portal.js';
import { GalleryVisualizer } from './visualizers/gallery.js';
import { ShojiVisualizer } from './visualizers/shoji.js';
import { SandVisualizer } from './visualizers/sand.js';
import { PrismVisualizer } from './visualizers/prism.js';
// RADIO: the recent visualizers. They were reaching the rotation (the
// orchestrator reads visualizer-registry.js, which is shared) but were
// absent from THIS file's own switcher list, which is a separate array
// from main.js's - so on the web radio they could not be chosen by hand at
// all and simply looked missing.
import { EchoVisualizer } from './visualizers/echo.js';
import { SilkVisualizer } from './visualizers/silk.js';
import { ShatterVisualizer } from './visualizers/shatter.js';
import { InfinityVisualizer } from './visualizers/infinity.js';
import { EmberVisualizer } from './visualizers/ember.js';
import { LichtenbergVisualizer } from './visualizers/lichtenberg.js';
import { OrigamiVisualizer } from './visualizers/origami.js';
import { SwarmVisualizer } from './visualizers/swarm.js';
import { NebulaVisualizer } from './visualizers/nebula.js';
import { OceanVisualizer } from './visualizers/ocean.js';
import { TuringVisualizer } from './visualizers/turing.js';
import { PyroVisualizer } from './visualizers/pyro.js';
import { CascadeVisualizer } from './visualizers/cascade.js';
import { MoshVisualizer } from './visualizers/mosh.js';
import { StrobeWallVisualizer } from './visualizers/strobewall.js';
import { LiquidLightVisualizer } from './visualizers/liquidlight.js';
import { KineticRig2Visualizer } from './visualizers/kinetic-rig2.js';
import { SteamVisualizer } from './visualizers/steam.js';
import { MaelstromVisualizer } from './visualizers/maelstrom.js';
import { NightdriveVisualizer } from './visualizers/nightdrive.js';
import { KaleidoVisualizer } from './visualizers/kaleido.js';
import { TesselVisualizer } from './visualizers/tessel.js';
import { FocusVisualizer } from './visualizers/focus.js';
import { PortalsVisualizer } from './visualizers/portals.js';
import { AdvectVisualizer } from './visualizers/advect.js';
import { RaveSignVisualizer } from './visualizers/ravesign.js';
import { AirframeVisualizer } from './visualizers/airframe.js';
import { VertigoVisualizer } from './visualizers/vertigo.js';
import { LanternVisualizer } from './visualizers/lantern.js';
import { Scope3DVisualizer } from './visualizers/scope3d.js';
import { getCandidate } from './visualizer-registry.js';
import { preloadGalleryManifest } from './gallery-assets.js';
import { preloadWarehouseScene } from './warehouse-assets.js';
import { preloadSunroomScene } from './sunroom-assets.js';
import { preloadPoolScene } from './poolside-assets.js';
import { preloadStadium } from './stadium-assets.js';
import { preloadPhotoStage } from './visualizers/shared/photoStage.js';
import { preloadHifi } from './hifi-assets.js';
import { preloadJukebox } from './jukebox-assets.js';
import { preloadWoofer } from './woofer-assets.js';
import { preloadStartrails } from './startrails-assets.js';
import { preloadGlowtide } from './glowtide-assets.js';
import { preloadPinball } from './pinball-assets.js';
import {
  initArtworkTransition, requestArtwork, warmArtwork, onArtworkTransitionStart,
  animateText, transitionHalo, setHaloImmediate
} from './artwork-transition.js';

// RADIO: the audio. radio.html sets window.__PIRADIO_STREAM_URL next to
// __PIRADIO_API_BASE (same origin as the page: /radio/stream, proxied by
// src/routes/radio.js from the Icecast mount). The player starts at once;
// #radio-status carries "Connecting to radio…" until audio flows (and the
// browser's own autoplay refusal, when it happens - see radio-player.js).
const STREAM_URL = (typeof window !== 'undefined' && window.__PIRADIO_STREAM_URL)
  || (location.origin + '/radio/stream');
const elVolumeLevel = document.getElementById('volume-indicator-level');
const radioPlayer = new RadioPlayer({
  audioEl: document.getElementById('radio-audio'),
  streamUrl: STREAM_URL,
  statusEl: document.getElementById('radio-status'),
  // Shown once, briefly, when the sound starts on a touch screen.
  playingHint: { text: 'Swipe left or right to change visual', ms: 7000, touchOnly: true },
  onVolume: function (level) {
    if (elVolumeLevel) elVolumeLevel.textContent = Math.round(level * 100) + '%';
    if (typeof externalRadio !== 'undefined') externalRadio.setVolume(level);
  },
  // body.radio-paused shows the play glyph over the cover (radio.html).
  // Une station externe joue ? Alors la pause du flux personnel ne dit rien de
  // ce qui est à l'écran - c'est elle qui l'occupe, et afficher son glyphe
  // « lecture » donnerait une station en pause qui joue.
  onPauseChange: function (paused) {
    document.body.classList.toggle('radio-paused', paused && !externalRadio.active);
  }
});
if (elVolumeLevel) elVolumeLevel.textContent = Math.round(radioPlayer.volume * 100) + '%';
// RADIO: /bbc2 opens straight on that station (see src/routes/radio.js and
// toggleExternalStation below); only then is the Soloist stream left alone
// until the listener presses B.
const STATION_FROM_PATH = (function () {
  // Served by the Pi the station is the path (judicael.com/rfm); bundled
  // into the TV app there is no path to speak of, so it is a parameter
  // (player.html?station=rfm) - see tizen-webradio-app.
  const q = new URLSearchParams(location.search).get('station');
  if (q && STATIONS[q.toLowerCase()]) return STATIONS[q.toLowerCase()];
  const m = /^\/([a-z0-9]+)$/i.exec(location.pathname);
  return m && STATIONS[m[1].toLowerCase()] ? STATIONS[m[1].toLowerCase()] : null;
})();
if (!STATION_FROM_PATH) radioPlayer.start();
// RADIO: external public stations (BBC Radio 2 on 'B'). While one plays, the
// Soloist stream is paused locally, the visualizer loop is stopped and the
// TV-style radio layer (radio.html #external-radio) takes the screen.
// RADIO: how this page was opened. ?news=0 drops the headline band (the TV
// app's own toggle); ?tv=1 says a remote is driving - the wording of the
// autoplay hint changes and OK goes back to the TV app's station grid.
const PAGE_PARAMS = new URLSearchParams(location.search);
const NEWS_ENABLED = PAGE_PARAMS.get('news') !== '0';
const TV_MODE = PAGE_PARAMS.get('tv') === '1';
if (TV_MODE) document.body.classList.add('tv-mode');

const externalRadio = new ExternalRadio({
  layer: document.getElementById('external-radio'),
  audio: document.getElementById('external-audio'),
  art: document.getElementById('ext-art-face'),
  artCard: document.getElementById('ext-art'),
  trackTitle: document.getElementById('ext-track-title'),
  trackArtist: document.getElementById('ext-track-artist'),
  programme: document.getElementById('ext-programme'),
  waves: document.getElementById('ext-radio-waves'),
  newsTicker: document.getElementById('ext-news-ticker'),
  newsTrack: document.getElementById('ext-news-track'),
  clock: document.getElementById('ext-clock'),
  clockTime: document.getElementById('ext-clock-time'),
  clockDate: document.getElementById('ext-clock-date'),
  stationName: document.getElementById('ext-station-name'),
  status: document.getElementById('radio-status')
}, {
  news: NEWS_ENABLED,
  gestureHint: TV_MODE ? 'Press OK on the remote to start the sound' : undefined
});
externalRadio.setVolume(radioPlayer.volume);
externalRadio.onPauseChange(function (paused) {
  document.body.classList.toggle('external-paused', paused);
  // In track mode the Soloist overlay's cover is what is on screen, so its
  // play glyph is the one to show.
  document.body.classList.toggle('radio-paused', paused && externalRadio.hasTrack);
});
document.getElementById('ext-art').addEventListener('click', function () { externalRadio.togglePause(); });
// A track (artist + title) identified by the station's API: the classic
// visualizer pipeline takes over - the profile source below hands it the
// track (features from TheAudioDB/Deezer, LLM danceability, cover palette,
// lyrics), the station screen keeps only its news/clock band. No track: the
// station screen comes back. See radio.html body.ext-track-mode.
// Vrai quand l'auditeur a demandé l'écran classique (flèche gauche).
let extClassicForced = false;
// Vrai quand c'est la PAGE qui a éteint les visuels, faute d'être regardée.
// Les deux se distinguent : au retour au premier plan on ne lève que le
// second, sinon on annulerait le choix de quelqu'un qui voulait l'écran
// classique.
let autoSuspended = false;
// Il a existé ici un bouton pour « retenir » l'animation en arrière-plan. Il
// a été retiré : le navigateur ne peint AUCUNE image d'une page cachée, quoi
// qu'on lui demande, et le bouton promettait donc ce qu'il ne pouvait pas
// tenir. La mise en veille automatique, elle, reste : elle fonctionne.
function visualsOff() { return extClassicForced || autoSuspended; }
// La pochette en cours sur la radio personnelle. Déclarée ICI, avant
// applyExtTrackMode qui la lit : plus bas, elle serait dans sa zone morte.
let lastArtworkUrl = null;
// GAUCHE quitte les visuels pour l'écran radio classique. DROITE les rappelle,
// puis, une fois qu'ils sont là, passe au visuel suivant du pool. La même règle
// vaut pour la radio personnelle et pour les stations externes : seul l'écran
// classique diffère (la pochette seule ici, l'écran de station là-bas).
//
// C'est un MODE et non un aperçu : il tient jusqu'à ce que Droite le lève. Un
// changement de titre ne le lève pas - sur la radio personnelle un titre dure
// trois minutes, et un mode qui s'évapore tout seul n'en est pas un.
//
// Nommée parce que trois choses l'appellent : les flèches, un glissement du
// doigt, et la télécommande de la TV (voir le pont de messages en fin de
// fichier).
function stepVisual(direction) {
  if (direction < 0) {
    if (!extClassicForced) { extClassicForced = true; applyExtTrackMode(); }
    else applyExtTrackMode();
    return;
  }
  if (visualsOff()) { extClassicForced = false; autoSuspended = false; applyExtTrackMode(); return; }
  if (externalRadio.active && !externalRadio.hasTrack) return;
  const av = sceneManager.activeVisualizer;
  if (av && typeof av.stepComposition === 'function') av.stepComposition(direction);
}
// LES CERCLES SE CENTRENT SUR LA POCHETTE, pas sur la mise en page.
//
// Les deux se centraient sur des repères différents : les ondes occupent
// l'écran moins le bandeau du bas, tandis que la pochette est poussée vers le
// haut par le titre et l'artiste écrits sous elle. D'où vingt-quatre pixels
// d'écart, visibles dès qu'on les regarde.
//
// Aucune règle CSS ne peut les accorder : la hauteur du texte dépend de la
// longueur du titre, qui change à chaque morceau. On mesure donc, et on
// décale. Sur une station externe, rien à faire : sa pochette est DANS la même
// boîte que les ondes.
function centreWavesOnArtwork() {
  const waves = document.getElementById('ext-radio-waves');
  if (!waves) return;
  // La cible dépend de l'écran : la carte de la station quand c'est son écran
  // d'attente qui est là, la pochette de l'overlay sur la radio personnelle.
  // Les deux sont poussées vers le haut par le texte écrit dessous, et les
  // ondes ne le savent pas.
  const carte = document.getElementById('ext-art');
  const visible = function (e) {
    if (!e) return false;
    const r = e.getBoundingClientRect();
    return r.height > 0 && getComputedStyle(e).visibility !== 'hidden';
  };
  const art = visible(carte) ? carte : document.getElementById('overlay-artwork');
  if (!art || !visible(art)) {
    waves.style.transform = '';
    return;
  }
  waves.style.transform = '';
  const w = waves.getBoundingClientRect();
  const a = art.getBoundingClientRect();
  if (!w.height || !a.height) return;
  const dy = (a.top + a.height / 2) - (w.top + w.height / 2);
  if (Math.abs(dy) > 0.5) waves.style.transform = 'translateY(' + Math.round(dy) + 'px)';
}

function applyExtTrackMode() {
  const off = visualsOff();
  const trackMode = externalRadio.active && externalRadio.hasTrack && !off;
  document.body.classList.toggle('ext-track-mode', trackMode);
  // La radio personnelle n'a pas d'écran de station : son écran classique,
  // c'est la pochette seule, les visuels éteints derrière (voir
  // body.classic-mode dans radio.html).
  const classic = !externalRadio.active && off;
  document.body.classList.toggle('classic-mode', classic);
  // Le décor de l'écran de station, prêté à la radio personnelle : ondes,
  // actualités, heure et nom. La pochette du titre reste au centre.
  if (classic) {
    externalRadio.startDecor(STATIONS[JAM_STATION_ID]);
    externalRadio.tintDecor(lastArtworkUrl);
  } else {
    externalRadio.stopDecor();
  }
  const wantScene = externalRadio.active ? trackMode : !classic;
  if (wantScene) sceneManager.start(); else sceneManager.stop();
  // Reste pour qui voudrait s'y accrocher en CSS : dit simplement si les
  // visuels tournent ou non.
  document.body.classList.toggle('visuals-off', !wantScene);
  if (typeof refreshSheetVisibility === 'function') refreshSheetVisibility();
  // Après le rendu : la pochette et les ondes doivent exister et être placées.
  requestAnimationFrame(centreWavesOnArtwork);
}
window.addEventListener('resize', centreWavesOnArtwork);
externalRadio.onTrackChange(function () {
  applyExtTrackMode();
});
if (STATION_FROM_PATH) {
  document.body.classList.add('external-radio');
  externalRadio.start(STATION_FROM_PATH);
}

function toggleExternalStation(station) {
  if (externalRadio.active && externalRadio.station.id === station.id) {
    externalRadio.stop();
    extClassicForced = false;
    document.body.classList.remove('external-radio', 'external-paused', 'ext-track-mode', 'radio-paused', 'classic-mode');
    sceneManager.start();
    radioPlayer.resume();
    if (typeof refreshSheetVisibility === 'function') refreshSheetVisibility();
    fetchJamQueue(true);
    return;
  }
  radioPlayer.pause();
  // Cette pause est celle du flux personnel, qui quitte l'écran : elle ne doit
  // pas y laisser le glyphe « lecture » par-dessus la station qui démarre.
  // (onPauseChange du lecteur se déclenche AVANT que la station soit active,
  // d'où ce nettoyage explicite juste après.)
  document.body.classList.remove('radio-paused');
  document.body.classList.add('external-radio');
  if (typeof refreshSheetVisibility === 'function') refreshSheetVisibility();
  document.body.classList.remove('external-paused');
  sceneManager.stop();
  externalRadio.start(station);
}

// How far the heard audio trails Soloist's reported position - see
// soloist-visual-profile.js. Printed in the bottom bar so a wrong guess is
// visible: ?latency=<s> to tune.
const RADIO_LATENCY_S = radioLatencySeconds();
const LATENCE_FORCEE = new URLSearchParams(location.search).get('latency') != null;
// Lissée : currentTime avance par pas et la mesure tremble de quelques
// centièmes ; un saut réel (une coupure de plusieurs secondes) passe en
// quelques sondages. Publiée pour les panneaux de debug.
let latenceLissee = null;
function latenceMesuree() {
  const r = radioPlayer && typeof radioPlayer.retardLecteurSecondes === 'function' ? radioPlayer.retardLecteurSecondes() : null;
  if (r == null) return latenceLissee;
  const brute = RADIO_LATENCE_FIXE_S + r;
  latenceLissee = latenceLissee == null || Math.abs(brute - latenceLissee) > 1.5 ? brute : latenceLissee + 0.35 * (brute - latenceLissee);
  window.__latenceRadio = latenceLissee;
  return latenceLissee;
}
// RADIO: the analysis comes from webradiopi (same cache as radiopi, audio
// recorded from Spotify's output on the Pi at first play) via this proxy.
const WEBRADIO_ANALYSIS_URL = API_BASE + '/api/webradio-analysis';
// trackId -> last `webradio.state` seen for it (awaiting_recording,
// recording, uploaded, analyzing, skipped, failed, cached). Module-level,
// not on the profile: buildProfile rebuilds that object every poll.
const webradioStateByTrack = new Map();

// Lyric-flash background effect (see lyric-flash.js/lyric-hook.js) -
// "mets de cote cette fonctionnalite pour le moment, le rendu n'est pas
// terrible". Paused rather than removed - the fetch trigger below is the
// single gate everything else (the flash layer, the footer's own
// readout) already depends on.
const LYRIC_FLASH_ENABLED = false;

const canvas = document.getElementById('party-canvas');
const elArtwork = document.getElementById('overlay-artwork');
const elArtworkA = document.getElementById('overlay-artwork-a');
const elArtworkB = document.getElementById('overlay-artwork-b');
initArtworkTransition(elArtworkA, elArtworkB);
// RADIO: a tap on the cover pauses this listener's audio, a second one
// resumes it at the live edge - see radio-player.js's pause()/resume().
// Whichever source is on: the external station in its track mode, else
// the Soloist stream.
elArtwork.addEventListener('click', function () {
  if (externalRadio.active) externalRadio.togglePause();
  else radioPlayer.toggle();
});
const elTitle = document.getElementById('overlay-title');
const elArtist = document.getElementById('overlay-artist');
const elStatus = document.getElementById('overlay-status');
const elPaletteDebug = document.getElementById('palette-debug');
const elCandidatesDebug = document.getElementById('candidates-debug');
const elLyricsSourceDebug = document.getElementById('lyrics-source-debug');
// ?candidates=1 - see visualizer-registry.js/orchestrator.js. On its own
// switch, independent of the 'd' debug-active toggle, so it can be checked
// straight from a URL without also turning on the switcher/FPS panels.
const CANDIDATES_DEBUG = new URLSearchParams(location.search).get('candidates') === '1';
if (CANDIDATES_DEBUG) {
  elCandidatesDebug.classList.add('visible');
  // Lets the lyrics-source panel move out from under it - both want top right,
  // and this one got there first.
  document.body.classList.add('candidates-active');
}
// ?marks=1 - radiopi mark taxonomy (see track-analysis.js/director.js).
// Same independent-of-'d' URL-param convention as ?candidates=1 above.
const elMarksDebug = document.getElementById('marks-debug');
const MARKS_DEBUG = new URLSearchParams(location.search).get('marks') === '1';
if (MARKS_DEBUG) elMarksDebug.classList.add('visible');
const elTrackFeaturesBar = document.getElementById('track-features-bar');
const elDanceDebug = document.getElementById('dance-debug');

// La barre technique vit dans track-debug.js, partagée avec l'écran de
// diffusion. Liée ICI et pas plus haut : la fabrique a besoin des trois
// éléments ci-dessus, et un `const` ne se hisse pas — la placer avant eux
// les rendrait nuls sans rien dire.
const barreTechnique = creerBarreTechnique({
  barEl: elTrackFeaturesBar,
  danceEl: elDanceDebug,
  lyricsEl: elLyricsSourceDebug,
  webradioStateByTrack: webradioStateByTrack
});
const renderTrackFeaturesBar = barreTechnique.render;
const renderDanceDebug = barreTechnique.renderDance;
const renderLyricsSourceDebug = barreTechnique.renderLyricsSource;

// RADIO: LE PANNEAU TECHNIQUE DE HALOFLOOR.COM (« debug » puis Entrée), le
// même que l'écran de diffusion — voir ecran-debug.js et play-boot.js : les
// paroles et l'analyse à droite, la ligne du morceau en bas, le nom du visuel
// dans le coin. Les anciens panneaux (barre du bas, palette, dansabilité,
// fps) restent dans la page mais ne s'affichent plus.
// trackId → la dernière réponse d'analyse (statut, route, cache, état de
// l'enregistrement), mise en forme comme /api/profil-musical la rend à
// halofloor : c'est ce que le bloc « Analyse » lit.
const profilParTitre = new Map();
function noterProfil(trackId, p) {
  profilParTitre.set(trackId, p);
  if (profilParTitre.size > 50) profilParTitre.delete(profilParTitre.keys().next().value);
}
const panneauTechnique = creerPanneauDebug({
  parolesEl: document.getElementById('debug-paroles'),
  analyseEl: document.getElementById('debug-analyse'),
  ligneEl: document.getElementById('debug-ligne'),
  profilParTitre: profilParTitre
});
function repeindrePanneau(pourTrackId) {
  const live = profileSource.getProfile();
  if (live && (pourTrackId == null || live.trackId === pourTrackId)) panneauTechnique.rendre(live);
}
// 'L' key toggle (see party-mode-controller.js's own _toggleLyricsBanner) -
// a transparent 3-line synced-lyrics banner. Independent of
// LYRIC_FLASH_ENABLED below (a different, currently-paused feature) and of
// the karaoke lyricHooks/repeated-phrase fetch further down - this one
// needs the FULL synced line list, not just the repeated-phrase subset.
const elLyricsBannerPrev2 = document.getElementById('lyrics-banner-prev2');
const elLyricsBannerPrev = document.getElementById('lyrics-banner-prev');
const elLyricsBannerCurrent = document.getElementById('lyrics-banner-current');
const elLyricsBannerNext = document.getElementById('lyrics-banner-next');
const elLyricsBannerNext2 = document.getElementById('lyrics-banner-next2');
const elLyricsUnavailable = document.getElementById('lyrics-unavailable');

const sceneManager = new SceneManager(canvas);

// QUAND FAUT-IL ÉCONOMISER ?
//
// Le mode économie coûte un peu de finesse et fait gagner beaucoup de
// batterie. On ne l'impose donc pas partout : on le pose là où l'énergie est
// comptée, et nulle part ailleurs.
//
//   - télévision : jamais. Grand écran, sur secteur, c'est là qu'on veut la
//     meilleure image ;
//   - téléphone et tablette : toujours. Ils sont sur batterie par définition,
//     et ce sont eux qui chauffent dans la main ;
//   - ordinateur : on demande à la machine. Débranché, on économise ;
//     rebranché, la finesse revient, sans recharger la page. Safari et Firefox
//     ne donnent pas cette information : dans le doute on ne dégrade rien, et
//     la flèche gauche reste là pour qui veut tout éteindre.
// Ce qu'on sait de l'alimentation. `null` tant qu'on ne sait pas : Safari et
// Firefox ne disent rien, et dans le doute on ne décide rien.
let onBattery = null;

function touchDevice() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;
}

function watchPower() {
  if (TV_MODE) return;

  if (touchDevice()) {
    // Un téléphone est sur batterie par définition.
    onBattery = true;
    sceneManager.setPowerMode(true);
    return;
  }

  if (!navigator.getBattery) return;
  navigator.getBattery().then(function (battery) {
    const apply = function () {
      onBattery = !battery.charging;
      sceneManager.setPowerMode(onBattery);
    };
    apply();
    battery.addEventListener('chargingchange', apply);
  }, function () { /* refusé : on ne dégrade rien */ });
}

watchPower();

// LES VISUELS NE TOURNENT QUE POUR QUELQU'UN QUI REGARDE.
//
// Une page de radio reste ouverte des heures derrière une autre fenêtre : elle
// y calculait soixante images par seconde pour personne. Dès que le navigateur
// passe en arrière-plan, l'écran classique prend le relais et la boucle de
// rendu s'arrête ; au retour, les visuels reviennent d'eux-mêmes.
//
// Deux signaux, parce qu'ils ne disent pas la même chose : `visibilitychange`
// couvre l'onglet caché ou la fenêtre réduite, `blur` couvre le cas où la
// fenêtre est encore visible mais derrière une autre. Le son, lui, continue.
function watchForeground() {
  if (TV_MODE) return;   // une télévision ne passe pas en arrière-plan

  const suspend = function () {
    // SUR ORDINATEUR, on n'éteint QUE sur batterie. Branché, rien ne presse :
    // l'écran d'à côté peut continuer de vivre, et couper l'animation à chaque
    // fois qu'on passe sur une autre fenêtre était plus gênant qu'utile.
    // Sur téléphone et tablette, on éteint toujours : l'énergie y est comptée.
    if (!touchDevice() && onBattery !== true) return;
    if (autoSuspended) return;
    autoSuspended = true;
    applyExtTrackMode();
  };
  const resume = function () {
    if (!autoSuspended) return;
    autoSuspended = false;
    applyExtTrackMode();
  };

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') suspend(); else resume();
  });
  window.addEventListener('blur', suspend);
  window.addEventListener('focus', resume);
}

watchForeground();


// Kicked off next to the renderer's own setup, not inside MainStage.init():
// the crowd sprite sheet is a static asset shared by every MainStage instance
// this session, so it is fetched once here and simply handed over below.
// Resolves to null on any failure, which MainStage degrades to wall + rig.
preloadCrowdAtlas();
preloadMatrixAtlas();   // matrix.js's glyph atlas
// Same reasoning as the crowd atlas above - resonance.js's wood-grain
// background is a static shared asset, fetched once here and handed over
// via options (never inside init()) so a swap into resonance never shows an
// empty frame while it loads. Resolves to null on any failure, which
// resonance.js degrades to rendering on plain black.
preloadSoundboardTexture();
preloadIconAtlas();
// Same reasoning again, for sumi.js's paper background.
preloadPaperTexture();
// The split-flap character atlas (tools/build-flapfont.mjs). Same contract
// as the textures above: preloaded once here, self-served by the visualizer,
// and a failure downgrades Splitflap to blank flaps rather than breaking it.
preloadFlapFont();
// Same reasoning again, for gallery.js - but the MANIFEST only (30KB of
// numbers). The photographs themselves are never preloaded: one decoded
// image is 5-27MB of GPU memory, so gallery.js loads exactly one, when it
// needs it. See gallery-assets.js.
// Withdraws Gallery from the rotation if its manifest is not there: it has
// nothing to show without photographs, and PROFILE.enabled is already read by
// the time this resolves. See orchestrator.js's disableVisualization.
preloadGalleryManifest().then(function (manifest) {
  if (!manifest) disableVisualization('gallery');
});
// Same for warehouse.js: without the hall photo and its depth map there is
// nothing to light.
preloadWarehouseScene().then(function (ok) {
  if (!ok) disableVisualization('warehouse');
});
// And sunroom.js: the room photo and its measured planes.
preloadSunroomScene().then(function (ok) {
  if (!ok) disableVisualization('sunroom');
});
// hifi.js: the two photographs of the hi-fi, or nothing to light.
preloadHifi().then(function (ok) {
  if (!ok) disableVisualization('hifi');
});
// Jukebox : sans une des deux photos (ou sans le build), indisponible
preloadJukebox().then(function (ok) {
  if (!ok) disableVisualization('jukebox');
});
// woofer.js: the two photographs of the speaker, or nothing to pump.
preloadWoofer().then(function (ok) {
  if (!ok) disableVisualization('woofer');
});
// stadium.js: the two photographs of the stadium, or nothing to light.
preloadStadium().then(function (ok) {
  if (!ok) disableVisualization('stadium');
});
// shared/photoStage.js: Headliner, Hangar, Goldenhour need their built scene (tools/build-photostage.mjs), or they are withdrawn.
preloadPhotoStage({ headliner: 'a', hangar: 'b', goldenhour: 'c' }).then(function (missing) {
  for (const id of missing) disableVisualization(id);
});
// startrails.js: the night arch and its sky, or nothing to show.
preloadStartrails().then(function (ok) {
  if (!ok) disableVisualization('startrails');
});
// glowtide.js: the night beach and its masks, or nothing to show.
preloadGlowtide().then(function (ok) {
  if (!ok) disableVisualization('glowtide');
});
// pinball.js: the playfield photos and their maps, or nothing to show.
preloadPinball().then(function (ok) {
  if (!ok) disableVisualization('pinball');
});
// poolside.js: the pool photo, its masks and its measured water quad.
preloadPoolScene().then(function (ok) {
  if (!ok) disableVisualization('poolside');
});
preloadHersheyFont();
preloadHersheySans();
preloadRosace();   // rosace.js's segmented rose window
preloadAlley();   // neonalley.js's two photographs of the alley
preloadFairground();   // fairground.js's two photographs and bulb map
// moonlake.js: the lake photo and its masks, or the visualizer is unavailable.
preloadMoonlake().then(function (ok) {
  if (!ok) disableVisualization('moonlake');
});
// cloudsea.js: the dawn, its masks and its flow, or the visualizer is unavailable
// (the sunrise is optional: without it the scene stays at dawn).
preloadCloudsea().then(function (ok) {
  if (!ok) disableVisualization('cloudsea');
});
// thunderhead.js: the dark photo, its light layers and masks, or the visualizer is unavailable
preloadThunderhead().then(function (ok) {
  if (!ok) disableVisualization('thunderhead');
});
// fountain.js: the lake photo and its masks, or the visualizer is unavailable.
preloadFountain().then(function (ok) {
  if (!ok) disableVisualization('fountain');
});
// seasons.js: the four aligned photos, masks and maps, or the visualizer is unavailable.
preloadSeasons().then(function (ok) {
  if (!ok) disableVisualization('seasons');
});
// tvwall.js: the photo of the wall and its screens; without them it falls back to its procedural wall.
preloadTvwall();
// balloons.js: the valley photo, its sky and proximity, or the visualizer is unavailable.
preloadBalloons().then(function (ok) {
  if (!ok) disableVisualization('balloons');
});
// hourglass.js: both photos, the mask, the reflections and the profile, or it is unavailable.
preloadHourglass().then(function (ok) {
  if (!ok) disableVisualization('hourglass');
});
preloadUnderground().then(function (ok) {   // underground.js's two photographs of the tunnel
  if (!ok) disableVisualization('underground');
});
preloadBayfrontSkyline();   // bayfront.js's Miami plate manifest
preloadBallroom().then(function (ok) {   // ballroom.js's two photographs and depth map
  if (!ok) disableVisualization('ballroom');
});
preloadSkyline();   // skyline.js's two photographs of the city
preloadArcade();   // arcade.js's two photographs of the arcade

// Which of the debug switcher's three columns a visualizer belongs in (see
// party-mode-controller.js's _buildPanel).
//
// The column is an EDITORIAL axis - "is this a party look, does it play over
// anything, is it for quiet music" - and no field in the codebase encodes it.
// PROFILE.energyLevel is the closest thing and is used as the default below,
// but it is only an approximation of this axis and it was wrong for seven of
// the twenty-nine entries when the panel was first grouped: PulseCore and
// ChromeFlux sit mid-scale on energy but read as club looks, while DrosteDive
// and Clave sit high on it and read as hypnotic and as rhythmic rather than
// as party; Halftone and Caustics are quieter than their numbers
// suggest. Those seven carry an explicit `group`, which always wins.
//
// So: `group` is the decision, energyLevel is the default for an entry that
// has not made one yet. Keeping the default means a visualizer added to the
// registry still lands somewhere sensible the day it is registered, instead
// of silently piling up in one column until someone notices.
//
// Two entries have no PROFILE at all, by deliberate registry decisions (see
// visualizer-registry.js's own exclusion list), so for them the explicit
// group is the only thing there is: AdaptiveParty is the orchestrated mode
// itself and plays over anything, NeonPulse is the party fallback it
// delegates to when track data is missing.
// La table des visualiseurs est SORTIE dans visualizer-table.js : l'écran de
// diffusion (play-boot.js) a besoin de la même, et deux copies de cent
// vingt-sept entrées divergent à la première addition.

const partyController = new PartyModeController(sceneManager, VISUALIZERS, {
  panelEl: document.getElementById('visualizer-switcher'),
  // RADIO: le panneau technique de halofloor n'a ni fps ni horaire.
  fpsEl: null,
  scheduleDebugEl: null,
  // RADIO: no visualizer-name badge on the public page, and the technical
  // panels only open by typing "debug" then Enter, never on a lone 'd'.
  vizNameEl: null,
  debugSequence: 'debug',
  // RADIO: on a phone, a horizontal swipe does what Left/Right do.
  swipe: true,
  volumeIndicatorEl: document.getElementById('volume-indicator'),
  volumeIndicatorIconEl: document.getElementById('volume-indicator-icon'),
  onLyricsToggle: handleLyricsToggle,
  startIndex: 0, // AdaptiveParty - the real music-data-driven party mode experience
  // profileSource is declared further down this module, but these
  // callbacks only ever run later (on a keypress, well after the whole
  // module has finished executing), so the closure over it is safe
  // despite the declaration order.
  // RADIO: Left/Right step through the orchestrator's own pool for this
  // track (AdaptivePartyVisualizer.stepComposition) - never to a visual it
  // would not have chosen, and never to another track. A forced switcher
  // entry (debug panel) has no pool to step through, so the press is idle.
  // RADIO: Space / play-pause key = the same local pause as a tap on the cover
  // (of whichever source is on screen).
  onPlayPause: function () {
    if (externalRadio.active) externalRadio.togglePause();
    else radioPlayer.toggle();
  },
  // RADIO: one letter per external station (and back) - see external-radio.js
  // STATIONS; the letter is each station's `key`.
  hotkeys: Object.keys(STATIONS).reduce(function (map, id) {
    if (STATIONS[id].key) map[STATIONS[id].key] = function () { toggleExternalStation(STATIONS[id]); };
    return map;
  }, {}),
  onVisualStep: stepVisual,
  // RADIO: this listener's own volume (the <audio> element), never Soloist's -
  // that one is the stream's master level and stays at 100%.
  onVolumeChange: function (direction) {
    radioPlayer.stepVolume(direction);
  }
});

// RADIO: le nom du visuel dans le coin bas droit du panneau technique. Le
// contrôleur sait écrire dans un élément ; on lui en donne un détaché et on
// recopie ce qu'il y met dans #viz-debug, qui n'apparaît qu'en mode debug
// (voir radio.html) — la page publique ne l'affiche jamais.
{
  const sonde = document.createElement('div');
  partyController.setVizNameEl(sonde);
  const nomEcran = document.getElementById('viz-debug');
  new MutationObserver(function () {
    if (nomEcran) nomEcran.textContent = sonde.textContent || '';
  }).observe(sonde, { childList: true, characterData: true, subtree: true });
}

// Read-only handle for the mandated scene-graph leak test (see the framing
// addendum): it needs to sample scene.children.length and
// renderer.info.memory across repeated AdaptiveParty swaps. Assignment only -
// nothing here reads it back, so it changes no behaviour.
window.__partyModeDebug = { sceneManager: sceneManager, radioPlayer: radioPlayer, externalRadio: externalRadio };

// RADIO: opened straight on a station, the visualizer loop stays off until
// the listener leaves the station (toggleExternalStation starts it then).
if (!STATION_FROM_PATH) sceneManager.start();

let lastTitleShown = null;
const TEXTE_ATTENTE_MAX_MS = 4000;
let texteEnAttente = null;
let texteAttenteTimer = null;
function appliquerTexte(title, artiste) {
  const titleChanged = title !== lastTitleShown;
  elTitle.textContent = title;
  elArtist.textContent = artiste;
  // "Si le titre est identique (meme morceau relance), ne l'anime pas."
  if (titleChanged && lastTitleShown !== null) animateText([elTitle, elArtist]);
  // Un blanc ne remplace pas la mémoire : c'est ce dernier titre connu qui
  // tient l'affichage pendant la seconde de silence entre deux morceaux.
  lastTitleShown = title || lastTitleShown;
  document.body.classList.remove('overlay-attente');
}
function appliquerTexteEnAttente() {
  clearTimeout(texteAttenteTimer);
  if (!texteEnAttente) { document.body.classList.remove('overlay-attente'); return; }
  const t = texteEnAttente; texteEnAttente = null;
  appliquerTexte(t.title, t.artist);
}

// The title is written IMMEDIATELY while the cover waits for its decode - they
// are deliberately not in lockstep. A title that lags a second behind the
// music to stay married to a picture is worse than the two arriving slightly
// apart, and the 80ms text stagger below is what makes the pair still read as
// one gesture on the normal path where the decode is fast.
function renderOverlay(profile) {
  // Same cleaned forms the stage displays (SplitFlap/TypeSlam) use: nothing
  // in brackets, nothing after a comma or a separator dash. The raw metadata
  // stays on the profile for lyrics/catalog lookups - only the DISPLAY is
  // cleaned.
  // Entre deux morceaux, Soloist ne dit plus rien pendant une seconde. Écrire
  // « Aucune lecture en cours » à ce moment-là est faux ET clignote : on garde
  // le dernier titre connu, et à défaut celui qui arrive.
  // RIEN D'ENTENDU, RIEN D'AFFICHÉ : sans morceau en cours (Soloist en pause
  // avant un enregistrement, page qui démarre), on ne remplit pas le vide
  // avec le prochain titre de la file — c'est ainsi que « Jamaican », titre
  // suivant, s'affichait sur une pochette vide au début des vidéos. Entre deux
  // morceaux, le dernier titre montré reste simplement en place.
  if (!cleanDisplayTitle(profile.title)) return;
  const title = cleanDisplayTitle(profile.title);
  // LE TITRE N'APPARAÎT QU'AVEC SA POCHETTE : à un changement de morceau, le
  // texte attend l'instant où la nouvelle pochette entre (voir
  // onArtworkTransitionStart) — l'ancien titre reste avec l'ancienne
  // pochette jusque-là, jamais un titre sur la mauvaise image. Sans pochette
  // qui arrive, le texte passe au bout de TEXTE_ATTENTE_MAX_MS.
  const artiste = cleanDisplayArtist(profile.artist) || '';
  if (profile.artworkUrl !== lastArtworkUrl) {
    texteEnAttente = { title: title, artist: artiste };
    clearTimeout(texteAttenteTimer);
    texteAttenteTimer = setTimeout(appliquerTexteEnAttente, TEXTE_ATTENTE_MAX_MS);
  } else if (!texteEnAttente) {
    appliquerTexte(title, artiste);
  } else {
    texteEnAttente = { title: title, artist: artiste };
  }

  // En mode classique, les ondes du décor suivent la pochette (sans effet
  // ailleurs : tintDecor ne fait rien tant que le décor n'est pas allumé).
  externalRadio.tintDecor(profile.artworkUrl || null);

  // Un titre sur deux lignes descend la pochette : les cercles suivent.
  requestAnimationFrame(centreWavesOnArtwork);

  if (profile.artworkUrl !== lastArtworkUrl) {
    lastArtworkUrl = profile.artworkUrl;
    // Nothing on screen changes here. requestArtwork decodes first and only
    // then crossfades, and it is what announces the transition instant to the
    // palette and the light rig (see onArtworkTransitionStart below).
    requestArtwork(profile.artworkUrl || null, { trackId: profile.trackId });
  }
}

// ONE instant, three consumers. Before this existed the palette was published
// the moment extraction finished and the rig dipped on the raw track change -
// both of which happen while the PREVIOUS cover is still on screen, which is
// exactly the "new image on the old colours" the brief describes (in fact it
// was the reverse: new colours under the old image).
onArtworkTransitionStart(function () {
  // la pochette entre : son titre avec elle
  appliquerTexteEnAttente();
  const profile = profileSource.getProfile();
  if (!profile) return;

  // Hand the visualizers the palette now, not when it was extracted.
  // SceneManager blends from the outgoing one over its own window, slightly
  // longer than the cover fade so the scene lands just after the picture.
  transitionFiredForTrackId = profile.trackId;
  if (pendingPalette && pendingPalette.trackId === profile.trackId) {
    publishPalette(pendingPalette.trackId, pendingPalette.palette);
    pendingPalette = null;
  } else if (!profile.artworkUrl) {
    // No cover at all on this track. The palette is now carried across track
    // changes (see sonos-visual-profile.js), so it has to be released
    // explicitly here or the previous cover's colours would outlive it.
    profileSource.setArtworkPalette(profile.trackId, null);
    transitionHalo(DEFAULT_HALO, applyHalo);
  }

  // The rig's 420ms dip. It used to key off profile.trackChangedAt, which is
  // stamped by the poll that first saw the new track - before the download.
  profileSource.setArtworkChangedAt(profile.trackId, Date.now());
});

// Held until the crossfade starts rather than published on arrival - see
// onArtworkTransitionStart above.
let pendingPalette = null;
// Which track the crossfade has already fired for, so a palette that arrives
// AFTER it is published straight away instead of waiting for an event that has
// already happened.
let transitionFiredForTrackId = null;
let paletteRequestTrackId = null;

function paletteRequestedFor(trackId) {
  return paletteRequestTrackId === trackId;
}

function artworkTransitionDone(trackId) {
  return transitionFiredForTrackId === trackId;
}

function publishPalette(trackId, palette) {
  const profile = profileSource.getProfile();
  if (!profile || profile.trackId !== trackId) return;
  // Blend FIRST: it reads the outgoing palette off the profile, so writing the
  // new one before it would leave it blending a colour into itself.
  if (!sceneManager.beginPaletteBlend(palette)) {
    profileSource.setArtworkPalette(trackId, palette);
  }
  transitionHalo(haloTarget(palette.dominantColors[0]), applyHalo);
}

// Same technique as tv.js's setRadioAccent: blend 55% toward white so
// the halo reads as a soft tinted glow rather than a saturated color
// patch, written as CSS custom properties the ::before/::after radial
// gradients in party-mode.html read from directly.
function lightenChannel(v) {
  return Math.round(v + (255 - v) * 0.55);
}

function haloTarget(hex) {
  return {
    r: lightenChannel((hex >> 16) & 0xff),
    g: lightenChannel((hex >> 8) & 0xff),
    b: lightenChannel(hex & 0xff)
  };
}

function applyHalo(color) {
  const root = document.documentElement.style;
  root.setProperty('--art-r', color.r);
  root.setProperty('--art-g', color.g);
  root.setProperty('--art-b', color.b);
}

// party-mode.html's own :root fallback, so the halo has somewhere to fade TO
// when a track has no cover instead of snapping back to the CSS default.
const DEFAULT_HALO = { r: 140, g: 227, b: 247 };

function setArtworkAccent(hex) {
  setHaloImmediate(haloTarget(hex), applyHalo);
}

function resetArtworkAccent() {
  const root = document.documentElement.style;
  root.removeProperty('--art-r');
  root.removeProperty('--art-g');
  root.removeProperty('--art-b');
}

// ?candidates=1 - lists every registered candidate (see
// visualizer-registry.js), enabled or not, with its CURRENT score against
// whatever track data is available right now, sorted best-first. The point
// is being able to check at a glance that nothing from the brief's table is
// silently missing - see orchestrator.js's own LIBRARY (enabled-only
// filter) for why a disabled entry can still show up here.
function renderCandidatesDebug(profile) {
  if (!CANDIDATES_DEBUG) return;
  const trackData = profile.trackFeatures || {};
  // Mirrors orchestrator.js's own requiresAnalysis exclusion. Without this the
  // panel would otherwise list a requiresAnalysis candidate on a track that
  // can never select it - a debug readout that disagrees with the thing it is
  // reporting on is worse than no readout.
  const rows = CANDIDATES.filter(function (c) {
    return !c.requiresAnalysis || (trackData && trackData.hasAnalysis);
  }).map(function (c) {
    return { c: c, score: scoreVisualization(c, trackData) };
  }).sort(function (a, b) { return b.score - a.score; });

  elCandidatesDebug.innerHTML = '';
  const title = document.createElement('div');
  title.textContent = 'candidates (' + CANDIDATES.filter(function (c) { return c.enabled; }).length + ' enabled / ' + CANDIDATES.length + ' total)';
  title.style.marginBottom = '0.3rem';
  elCandidatesDebug.appendChild(title);

  const table = document.createElement('table');
  rows.forEach(function (row) {
    const tr = document.createElement('tr');
    if (!row.c.enabled) tr.classList.add('disabled');
    [row.c.id, row.c.family, row.score.toFixed(3), row.c.enabled ? '' : 'disabled'].forEach(function (text) {
      const td = document.createElement('td');
      td.textContent = text;
      tr.appendChild(td);
    });
    table.appendChild(tr);
  });
  elCandidatesDebug.appendChild(table);
}

// Step 5 validation harness: renders swatches + stats on screen so the
// CORS/extraction check can be confirmed by eye on the actual TV, not
// just by inference from the (already-proven) tv.js mesh background
// reusing the same /api/art-proxy approach.
function renderPaletteDebug(palette, err) {
  if (err) {
    elPaletteDebug.textContent = 'Palette: ' + err.message;
    elPaletteDebug.style.color = '#ff8080';
    return;
  }
  elPaletteDebug.style.color = '';
  elPaletteDebug.innerHTML = '';
  palette.dominantColors.forEach(function (hex) {
    const swatch = document.createElement('span');
    swatch.style.display = 'inline-block';
    swatch.style.width = '14px';
    swatch.style.height = '14px';
    swatch.style.marginRight = '4px';
    swatch.style.borderRadius = '3px';
    swatch.style.background = '#' + hex.toString(16).padStart(6, '0').slice(-6);
    elPaletteDebug.appendChild(swatch);
  });
  const vividLabel = document.createElement('div');
  vividLabel.textContent = 'vivid:';
  vividLabel.style.marginTop = '4px';
  elPaletteDebug.appendChild(vividLabel);
  palette.vividColors.forEach(function (hex) {
    const swatch = document.createElement('span');
    swatch.style.display = 'inline-block';
    swatch.style.width = '14px';
    swatch.style.height = '14px';
    swatch.style.marginRight = '4px';
    swatch.style.borderRadius = '3px';
    swatch.style.background = '#' + hex.toString(16).padStart(6, '0').slice(-6);
    elPaletteDebug.appendChild(swatch);
  });
  const stats = document.createElement('div');
  stats.textContent =
    'brightness ' + palette.brightness.toFixed(2) +
    ' · saturation ' + palette.saturation.toFixed(2) +
    ' · warmth ' + palette.warmth.toFixed(2);
  elPaletteDebug.appendChild(stats);
}

// The discrete bottom-of-screen readout (see #track-features-bar in
// party-mode.html) driving/explaining AdaptivePartyVisualizer's choices -
// "affiche titre, artiste, BPM, energy, valence, danceability, mood",
// updated on every poll so it reflects whatever's actually resolved so
// far (title/artist immediately, the Deezer/TheAudioDB fields once
// fetchTrackFeatures resolves - see onUpdate below).
// "aucunes paroles [...] mots captures" - once lyricsResolved, either
// show every phrase lyric-hook.js picked (joined - there can be up to
// 3, see findRepeatedPhrases) or, if the lookup finished but genuinely
// found nothing (LRCLIB has no synced lyrics for this track), say so
// plainly rather than just silently omitting the segment. Still resolving
// (lyricsResolved:false) shows nothing here, same "don't flash a
// not-yet-true negative" reasoning as the mic button's own loading state.
// The three danceability figures, all three shown at all times.
//
// They are not the same measurement and that is the entire reason they sit
// together rather than one replacing another in a segment:
//
//   deezer   Deezer/TheAudioDB's own 0-100 figure, shown here rescaled to
//            0-1 so it can be read against the other two. A different
//            quantity from a different corpus - it is the outside opinion.
//   llm      radiopi's model estimate, from title and artist alone. Lands
//            within a second or two of the track starting.
//   radiopi  radiopi's measurement on the actual audio, ~80s in. Definitive.
//
// The arrow marks which one the client is actually using right now, i.e.
// whichever radiopi currently reports as authoritative. A row reading "—"
// means not resolved yet, and stays visible rather than collapsing, so the
// box never changes shape while a track loads.


// EXACTLY which lyrics these are. There is no lyrics file to name - LRCLIB is
// a JSON search API - so what identifies the words is the record they came
// from: its LRCLIB id, and the track/artist/album/duration it is filed under.
//
// The reason this is worth a line in the bar: the lookup runs a CASCADE that
// deliberately loosens its query, title+artist first and bare title last (see
// lyricsService.js's runSearchCascade). A song whose words are nearly right is
// almost always one that only matched on the loosest attempt, and that is
// invisible from the words alone. `q2` here means the bare-title attempt won.
//
// Renders nothing until the lookup has actually replied, same "don't show a
// not-yet-true state" rule fmtLyricSegment and fmtApiStatusSegment follow.


// Which data source is currently resolving/resolved for this track (see
// profile.apiStatus / setApiStatus in main.js's own fetch chain below) -
// a quiet "why is it doing this" readout, same spirit as the rest of this
// bar, not real UI. null (nothing attempted yet, e.g. before title/artist
// or duration are known) renders nothing, same "don't show a not-yet-true
// state" rule fmtLyricSegment already follows.
// RADIO: what webradiopi says about the first-play recording, in front of
// the generic API status - "première lecture" is the thing to see here.




// Upcoming forced-swap marks (segment-level transitions + high-confidence
// drops, see track-analysis.js/adaptive-party.js's own intensityChangeInterrupt)
// - recomputed straight from profile.trackAnalysis rather than read off
// AdaptivePartyVisualizer's own live pointer, so this stays correct
// regardless of which entry the debug switcher currently has active.
// Empty (no segment) when there's no analysis, or nothing left ahead of
// the current position.
// profile.position is the raw Sonos value (frozen between its own ~3s
// polls) - profile.estimatedPosition (scene-manager.js's PositionSmoother,
// written every render frame) is what both segments below read instead,
// so this bar's own numbers actually match what adaptive-party.js's
// forced-swap detection is reacting to in real time, not a stale 3s-old
// snapshot.

// Where we are in the track's own structure, as radiopi's analysis segmented
// it: intro / build / drop / breakdown / sustain / outro (see
// track-analysis.js's lookupMode). This is the half of the bottom line that
// changes as the track plays, and it is what the orchestrator's own mode-based
// choices key off - so seeing it wrong here is how you catch a bad segmentation.

// The mix points the analysis identified: where the track is actually usable
// from and until (mix-in / mix-out, i.e. past the intro and before the outro),
// its drops, and the next structural event coming up.
//
// Two different things deliberately sit together here. mix-in/mix-out and the
// drops are FIXED properties of the track, known as soon as it is analysed;
// "next" is relative to the playhead and moves. Reading them side by side is
// what tells you whether an upcoming event is one the analysis really found or
// an artefact of where the playhead happens to be.


// Lets the "next changes at mm:ss" segment above be checked by eye
// against where playback actually is right now, rather than trusting it
// blind. Position only (no "/ duration") when duration is unknown (radio
// - see director.js's own duration===0 handling), since "mm:ss / —"
// would imply a real total that doesn't exist.


// See the fetchTrackAnalysis guard inside onUpdate below for why this
// can't just be a "!profile.trackAnalysis" check on the profile itself.
// --- INSTRUMENTATION TEMPORAIRE : délai avant l'appel radiopi ---------------
// Pose la question « pourquoi radiopi met 18 s alors que le serveur répond en
// 0,18 s ». Le serveur et /api/track-features ont été mesurés hors de cause ;
// le suspect restant est l'attente de `profile.duration`, que Sonos ne fournit
// pas toujours au premier poll après un changement de titre.
//
// À RETIRER une fois le diagnostic fait (chercher « radiopi-trace »).
let traceTrackId = null;
let traceStartedAt = 0;
let tracePolls = 0;
function traceAnalysisDelay(profile) {
  if (profile.trackId !== traceTrackId) {
    traceTrackId = profile.trackId;
    traceStartedAt = Date.now();
    tracePolls = 0;
    console.log('[radiopi-trace] t=0ms CHANGEMENT id=' + profile.trackId
      + ' duration=' + JSON.stringify(profile.duration)
      + ' titre=' + JSON.stringify(profile.title)
      + ' artiste=' + JSON.stringify(profile.artist)
      + ' album=' + JSON.stringify(profile.album));
    return;
  }
  // Une fois la requête partie, plus rien à observer pour ce titre.
  if (trackAnalysisFetchTrackId === profile.trackId) return;
  tracePolls += 1;
  console.log('[radiopi-trace] t=' + (Date.now() - traceStartedAt) + 'ms poll#' + tracePolls
    + ' EN ATTENTE duration=' + JSON.stringify(profile.duration)
    + ' titre=' + JSON.stringify(profile.title)
    + ' album=' + JSON.stringify(profile.album));
}
function traceSince() { return Date.now() - traceStartedAt; }

let trackAnalysisFetchTrackId = null;
// Guards the parallel TheAudioDB lookup the same way, and tracks which
// track radiopi has already won bpm for (see the onUpdate fetch block).
let quickFeaturesFetchTrackId = null;
let analyzedTrackId = null;

// 'L'-toggled lyrics banner state - the full synced line list (not just
// lyricHooks' repeated-phrase subset) for whichever track this was last
// fetched for, plus the same fetch-once-per-track guard pattern as
// trackAnalysisFetchTrackId above.
let lyricsBannerLines = [];
let lyricsBannerFetchTrackId = null;

// Drives body.lyrics-no-lyrics (see party-mode.html's overlay CSS). The 'L'
// toggle says the user WANTS lyrics; this says whether this particular track
// HAS any. Only when both hold does the cover move aside to free the middle of
// the screen - otherwise moving it would clear space for an empty banner.
//
// Recomputed per track and never sticky, so "le temps de la chanson
// uniquement" falls out of the state itself rather than needing a timer: the
// next track re-runs the fetch and re-runs this.
function syncLyricsAvailability() {
  document.body.classList.toggle('lyrics-no-lyrics', lyricsBannerLines.length === 0);
}
syncLyricsAvailability();

const LYRICS_UNAVAILABLE_VISIBLE_MS = 3000;
let lyricsUnavailableHideTimer = null;
function showLyricsUnavailable() {
  elLyricsUnavailable.classList.add('visible');
  clearTimeout(lyricsUnavailableHideTimer);
  lyricsUnavailableHideTimer = setTimeout(function () {
    elLyricsUnavailable.classList.remove('visible');
  }, LYRICS_UNAVAILABLE_VISIBLE_MS);
}

// PartyModeController's own onLyricsToggle callback (see below) - 'L'/
// center-OK reaches here instead of toggling body.lyrics-active blindly,
// so a track with nothing to show gets a clear "Paroles indisponibles"
// notice instead of silently opening an empty banner.
function handleLyricsToggle() {
  if (lyricsBannerLines.length === 0) {
    showLyricsUnavailable();
    return;
  }
  document.body.classList.toggle('lyrics-active');
}

// Same tolerance/lookup shape as tv.js's own findActiveLyricsIndex -
// mirrors that page's existing synced-lyrics feature rather than
// reinventing the matching logic. lines: [{time, text}, ...] as returned
// by /api/lyrics (see lyrics-client.js), already sorted by time.
const LYRICS_BANNER_TOLERANCE_SEC = 0.2;

// Per-source lyrics timing correction, in seconds. POSITIVE shows a line
// earlier, NEGATIVE shows it later.
//
// The position Sonos reports and the audio you actually hear are not the same
// instant, and the gap has a different sign per source - reported from real
// listening, not derived:
//
//   direct (Sonos plays it itself) - lines landed about a second LATE, so they
//     are pulled forward. The lag is the poll's own round trip: reanchor()
//     timestamps with performance.now() at RECEPTION while elapsedTime
//     describes when the Sonos API answered, so the whole clock sits behind.
//
//   stream (AirPlay / Spotify Connect) - lines landed about a second EARLY, so
//     they are pushed back. Opposite cause: the speaker BUFFERS what is pushed
//     into it, so its reported position runs ahead of what leaves the driver.
//
// Applied to the LYRICS ONLY, not to the bar's clock or to the beat grid. The
// same skew is on those too, but the beat grid is periodic - at 120 BPM a
// one-second error is exactly two beats, i.e. no phase error at all - so
// correcting it there would be changing something nobody can see, on the
// strength of two numbers measured by ear.

// ?lyricsoffset=<seconds> overrides the table above, for tuning by eye
// without a rebuild. Applies to whichever source is active.


function findActiveLyricsBannerIndex(lines, elapsedSeconds, offsetSeconds) {
  const cutoff = elapsedSeconds + LYRICS_BANNER_TOLERANCE_SEC + (offsetSeconds || 0);
  let idx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].time <= cutoff) idx = i;
    else break;
  }
  return idx;
}

function renderLyricsBanner(profile) {
  if (lyricsBannerLines.length === 0) {
    elLyricsBannerPrev2.textContent = '';
    elLyricsBannerPrev.textContent = '';
    elLyricsBannerCurrent.textContent = '';
    elLyricsBannerNext.textContent = '';
    elLyricsBannerNext2.textContent = '';
    return;
  }
  const idx = findActiveLyricsBannerIndex(
    lyricsBannerLines, currentPositionSeconds(profile), lyricsOffsetSeconds(profile));
  const at = function (i) {
    return i >= 0 && i < lyricsBannerLines.length ? lyricsBannerLines[i].text : '';
  };
  // idx is -1 before the first verse: at() returns '' for every negative
  // slot on its own, and next/next2 then preview the opening lines - same
  // behaviour the 3-line banner had.
  elLyricsBannerPrev2.textContent = at(idx - 2);
  elLyricsBannerPrev.textContent = at(idx - 1);
  elLyricsBannerCurrent.textContent = at(idx);
  elLyricsBannerNext.textContent = at(idx + 1);
  elLyricsBannerNext2.textContent = at(idx + 2);
}

// ?seek=<ms> - jumps playback position once, right after the first poll
// resolves, so a known mark (see ?marks=1) can be reached without waiting
// out the real track - "tester un drop sans attendre". One-shot: the very
// next real Sonos poll (~3s later) naturally corrects position back to
// wherever playback actually is, same as any other manual seek would.
const SEEK_MS = (function () {
  const raw = parseFloat(new URLSearchParams(location.search).get('seek'));
  return isNaN(raw) ? null : raw;
})();
let seekApplied = false;

// ?marks=1 - the full radiopi mark list (type/score) plus time-until-next,
// same info _handleMark dispatches on (see adaptive-party.js). Rebuilt
// each tick rather than diffed - a handful of marks, cheap either way.
function renderMarksDebug(profile) {
  if (!MARKS_DEBUG) return;
  if (!profile.analysisTimeline) {
    elMarksDebug.textContent = 'marks: aucune analyse (repli)';
    return;
  }
  const nowMs = currentPositionSeconds(profile) * 1000;
  const lines = profile.analysisTimeline.marks.map(function (m) {
    const past = m.atMs <= nowMs;
    const marker = past ? '✓' : (fmtClock(Math.max(0, m.atMs - nowMs)));
    return (past ? '  ' : '> ') + fmtClock(m.atMs) + '  ' + m.type.padEnd(7) + ' ' + m.score.toFixed(3) + '  ' + marker;
  });
  elMarksDebug.textContent = 'marks (t=' + fmtClock(nowMs) + '):\n' + lines.join('\n');
}

// Paints the debug panel's yellow marks: exactly the visualizers radiopi's
// danceability index lets the orchestrator choose between for this track. The
// SAME function the orchestrator calls, on the same default library, so what
// is highlighted is what is actually eligible rather than a second guess at it.
function renderDancePool(profile) {
  // Always a number now (0.5 until radiopi says otherwise - see
  // resolveDanceabilityIndex), so there is no unrestricted case left to paint.
  const index = resolveDanceabilityIndex(profile);
  const seed = profile ? String(profile.trackId) + '|' + String(profile.trackChangedAt) : '';
  partyController.setPoolIds(
    selectDanceabilityPool(libraryForTrack(porteDepuisProfil(profile)), index, undefined, seed).map(function (v) { return v.id; }));
}

// RADIO: Soloist's now-playing, latency-corrected - see
// soloist-visual-profile.js. Same onUpdate body as main.js from here on.
const profileSource = createSoloistVisualProfileSource(
  {
    // LA LATENCE MESURÉE (voir soloist-visual-profile.js) : la part fixe plus
    // le temps que ce lecteur a passé sans jouer. ?latency=<s> force encore
    // une valeur fixe, pour régler à l'oeil.
    latencySeconds: LATENCE_FORCEE ? RADIO_LATENCY_S : latenceMesuree,
    // External station on: its identified track (or nothing) replaces the
    // Soloist state as the visualizer's input. See external-radio.js.
    override: function () {
      return externalRadio.active ? externalRadio.profileState() : undefined;
    }
  },
  function onUpdate(profile) {
    traceAnalysisDelay(profile);
    elStatus.textContent = ''; // clear any stale error once a poll succeeds
    if (SEEK_MS != null && !seekApplied && profile.trackId) {
      seekApplied = true;
      profile.position = SEEK_MS / 1000;
      profile.progress = profile.duration > 0 ? Math.min(1, Math.max(0, profile.position / profile.duration)) : 0;
    }
    renderOverlay(profile);
    renderTrackFeaturesBar(profile);
    renderCandidatesDebug(profile);
    renderDancePool(profile);
    renderLyricsBanner(profile);
    renderMarksDebug(profile);
    panneauTechnique.rendre(profile);
    // RADIO: une station extérieure n'a pas de durée, donc pas d'analyse :
    // le bloc « Analyse » le dit au lieu d'attendre.
    if (profile.trackId && !(profile.duration > 0) && !profilParTitre.has(profile.trackId)) noterProfil(profile.trackId, { status: 'unavailable' });
    sceneManager.setProfile(profile);

    if (profile.title && profile.artist && lyricsBannerFetchTrackId !== profile.trackId) {
      const bannerTrackId = profile.trackId;
      lyricsBannerFetchTrackId = bannerTrackId;
      // Dropped BEFORE the fetch, not just on its result: they belong to the
      // previous track, and leaving them in place showed the last song's words
      // (and held the moved-aside layout) for as long as the request took.
      lyricsBannerLines = [];
      syncLyricsAvailability();
      renderLyricsBanner(profile);
      fetchLyrics(profile.title, profile.artist, profile.duration > 0 ? profile.duration : null).then(function (result) {
        // The source is recorded even when the track has moved on: it costs
        // nothing and the setter is keyed on trackId anyway, so a stale reply
        // simply lands nowhere.
        profileSource.setLyricsSource(bannerTrackId,
          result && result.source, result && result.query);
        if (profile.trackId !== bannerTrackId) return; // track changed mid-fetch - stale, discard
        lyricsBannerLines = (result && result.found && result.lines) || [];
        syncLyricsAvailability();
        renderLyricsBanner(profileSource.getProfile());
        renderTrackFeaturesBar(profileSource.getProfile());
      });
    }

    // Keyed on the TRACK, not on artworkPalette being null. It used to be the
    // latter, which worked only because the palette was wiped on every track
    // change; now that it is carried across (see sonos-visual-profile.js, so
    // the old colours survive until the new cover actually appears) a
    // null-check would never fire again and no track would ever get a new
    // palette. paletteRequestTrackId is the fetch-once-per-track guard.
    if (profile.artworkUrl && !paletteRequestedFor(profile.trackId)) {
      const trackId = profile.trackId;
      paletteRequestTrackId = trackId;
      extractArtworkPalette(profile.artworkUrl).then(
        function (palette) {
          renderPaletteDebug(palette, null);
          // NOT published here any more. The scene must not change colour
          // while the previous cover is still on screen, so the palette waits
          // for the crossfade to start (see onArtworkTransitionStart).
          //
          // The two can finish in either order, and both orders are real: a
          // cached cover decodes in a few ms and beats the extraction, a cold
          // one does not. If the transition already ran, publish immediately;
          // otherwise park it and let the transition pick it up.
          if (artworkTransitionDone(trackId)) {
            publishPalette(trackId, palette);
          } else {
            pendingPalette = { trackId: trackId, palette: palette };
          }
        },
        function (err) {
          console.error('[party-mode] artwork palette extraction failed', err);
          renderPaletteDebug(null, err);
        }
      );
    }

    // Both lookups fire in PARALLEL on track change, not sequentially -
    // radiopi /analyze takes ~60-100s on a cache miss (see
    // track-analysis-client.js's own retry loop), and waiting for it
    // before even trying TheAudioDB left the orchestrator with NO bpm at
    // all for up to a minute on every uncached track. TheAudioDB
    // (fetchTrackFeatures) is the fast one (~1-2s) - it fills bpm/energy/
    // danceability/valence/mood immediately, then radiopi's bpm overrides
    // just the bpm field once/if it resolves (still preferred - more
    // accurate than Deezer's own estimate - just no longer the ONLY
    // source in the meantime). analyzedTrackId tracks whether radiopi has
    // already won for this track, so a late-resolving TheAudioDB response
    // doesn't clobber a bpm radiopi already set.
    if (profile.title && profile.artist && quickFeaturesFetchTrackId !== profile.trackId) {
      const trackId = profile.trackId;
      quickFeaturesFetchTrackId = trackId;
      profileSource.setApiStatus(trackId, 'quick');
      renderTrackFeaturesBar(profileSource.getProfile());
      fetchTrackFeatures(profile.title, profile.artist, profile.duration > 0 ? profile.duration : null).then(
        function (features) {
          if (profile.trackId !== trackId) return;
          const existingFeatures = profileSource.getProfile().trackFeatures || {};
          let merged;
          if (analyzedTrackId === trackId) {
            merged = Object.assign({}, features, { bpm: existingFeatures.bpm, bpmSource: existingFeatures.bpmSource });
          } else if (features.bpm == null && existingFeatures.bpm != null) {
            // RADIO: Deezer had no tempo; keep the model's estimate.
            merged = Object.assign({}, features, { bpm: existingFeatures.bpm, bpmSource: existingFeatures.bpmSource });
          } else {
            merged = Object.assign({}, features, { bpmSource: features.bpm != null ? 'deezer' : null });
          }
          profileSource.setTrackFeatures(trackId, merged);
          if (analyzedTrackId !== trackId) {
            console.log('[radiopi-trace] t=' + traceSince() + 'ms libellé « radiopi en cours… » affiché');
            profileSource.setApiStatus(trackId, 'quick-ok');
          }
          renderTrackFeaturesBar(profileSource.getProfile());
          renderCandidatesDebug(profileSource.getProfile());
        },
        function (err) {
          console.error('[party-mode] track features lookup failed', err);
          if (profile.trackId === trackId && analyzedTrackId !== trackId) {
            profileSource.setApiStatus(trackId, 'failed');
            renderTrackFeaturesBar(profileSource.getProfile());
          }
        }
      );
    }

    // radiopi /analyze - see track-analysis.js. duration is required by
    // that API; a web radio (duration===0, director.js's own handling)
    // simply never gets this second lookup and relies on the quick one
    // above alone, same as before this feature existed. Guarded by
    // trackAnalysisFetchTrackId (module-level, not on the profile) rather
    // than "!profile.trackAnalysis" alone: a fresh (non-cached) analysis
    // can take up to ~100s, and the Sonos poll driving onUpdate runs
    // every few seconds - without this guard every poll tick in that
    // window would fire ANOTHER concurrent request for the same track.
    if (profile.title && profile.artist && profile.duration > 0 && trackAnalysisFetchTrackId !== profile.trackId) {
      const trackId = profile.trackId;
      trackAnalysisFetchTrackId = trackId;
      console.log('[radiopi-trace] t=' + traceSince() + 'ms REQUÊTE PART'
        + ' duration=' + JSON.stringify(profile.duration));
      const traceRequestAt = Date.now();
      // The callback fires on the FIRST response - a 202 already carries the
      // LLM's estimate - and again when the measurement replaces it, so the
      // bar shows an index within a second instead of after the analysis.
      fetchTrackAnalysis(profile.title, profile.artist, profile.duration, function (danceability) {
        console.log('[radiopi-trace] t=' + traceSince() + 'ms RÉPONSE'
          + ' (' + (Date.now() - traceRequestAt) + 'ms de requête)'
          + ' source=' + (danceability && danceability.source));
        if (profileSource.getProfile().trackId !== trackId) return;  // stale
        profileSource.setDanceability(trackId, danceability);
        // RADIO: the model also states the tempo when it knows the song. It
        // fills the BPM only while no catalogue (Deezer) or analysis has one
        // - those override it as soon as they land (see the merges below).
        if (danceability && danceability.llmBpm) {
          const cur = profileSource.getProfile().trackFeatures;
          if (!cur || cur.bpm == null) {
            profileSource.setTrackFeatures(trackId, Object.assign({}, cur || {}, { bpm: danceability.llmBpm, bpmSource: 'llm' }));
          }
        }
        // Repaint immediately rather than waiting for the next 3s poll: the
        // index is what decides the pool, and the panel showing a stale one
        // for three seconds is exactly when someone is looking at it.
        const live = profileSource.getProfile();
        if (live && live.trackId === trackId) renderDancePool(live);
        renderTrackFeaturesBar(profileSource.getProfile());
        repeindrePanneau(trackId);
      }, {
        // RADIO: webradiopi instead of radiopi, plus its first-play state.
        url: WEBRADIO_ANALYSIS_URL,
        // Le statut de l'analyse pour le panneau technique, dans la forme de
        // /api/profil-musical : ok / processing / error, route, niveau de
        // cache, état de l'enregistrement, code d'erreur.
        onResponse: function (json, status) {
          const j = json || {};
          noterProfil(trackId, {
            status: status === 200 ? 'ok' : status === 202 ? 'processing' : 'error',
            route: 'webradiopi',
            cache_level: j.track && j.track.cache_level ? j.track.cache_level : null,
            webradio: externalRadio.active ? null : (j.webradio || null),
            error: j.error ? (j.error.code || String(j.error.message || j.error)) : null
          });
          repeindrePanneau(trackId);
        },
        onState: function (webradio) {
          // An external station's track is never recorded by the Pi (that
          // recorder follows Soloist), so its "awaiting recording" state
          // would only mislead; the LLM danceability is what is used.
          if (externalRadio.active) return;
          webradioStateByTrack.set(trackId, webradio && webradio.state);
          if (webradioStateByTrack.size > 50) webradioStateByTrack.delete(webradioStateByTrack.keys().next().value);
          const live = profileSource.getProfile();
          if (live && live.trackId === trackId) renderTrackFeaturesBar(live);
        }
      }).then(function (analysis) {
        if (profile.trackId !== trackId) return; // track changed mid-fetch - stale, discard
        if (!analysis || analysis.found === false) {
          // Échec définitif (NOT_FOUND, entrée invalide) ou budget de polls
          // épuisé. Le client ne peut pas distinguer les deux, mais dans les
          // deux cas il ne se passera plus rien : il faut le DIRE, sinon le
          // libellé reste bloqué sur « en cours ».
          console.log('[radiopi-trace] t=' + traceSince() + 'ms ABANDON (aucune analyse)');
          if (analyzedTrackId !== trackId) {
            profileSource.setApiStatus(trackId, 'quick-only');
            renderTrackFeaturesBar(profileSource.getProfile());
          }
          return;
        }
        analyzedTrackId = trackId;
        profileSource.setTrackAnalysis(trackId, analysis);
        repeindrePanneau(trackId);
        // Keep whatever the quick fetch already set (energy/danceability/
        // valence/mood - radiopi provides none of those), just override bpm.
        const existing = profileSource.getProfile().trackFeatures || {};
        profileSource.setTrackFeatures(trackId, Object.assign({}, existing, { bpm: bpmFromAnalysis(analysis), bpmSource: 'analyse' }));
        profileSource.setApiStatus(trackId, 'analyzed');
        renderTrackFeaturesBar(profileSource.getProfile());
        renderCandidatesDebug(profileSource.getProfile());
      });
    }

    // Same pattern again for the lyric-flash background effect (see
    // lyric-flash.js) - synced-lyrics lookup + repeated-phrase analysis
    // on track change, result attached back onto the profile so
    // scene-manager.js's flash layer and this file's own footer readout
    // both just read profile.lyricHooks rather than each fetching it.
    // Paused - "le rendu n'est pas terrible" - without ever triggering
    // the fetch, profile.lyricHooks/lyricsResolved simply never get set,
    // which already keeps both the flash layer (scene-manager.js) and
    // the footer's own "paroles: ..." segment (renderTrackFeaturesBar
    // above) silent - no separate toggle needed in either of those
    // files. Flip LYRIC_FLASH_ENABLED back on to resume.
    if (LYRIC_FLASH_ENABLED && profile.title && !profile.lyricsResolved) {
      const trackId = profile.trackId;
      fetchLyrics(profile.title, profile.artist, profile.duration > 0 ? profile.duration : null).then(function (result) {
        const found = !!(result && result.found);
        const lines = (result && result.lines) || [];
        const hooks = found && lines.length > 0 ? findRepeatedPhrases(lines, profile.title) : [];
        profileSource.setLyricHooks(trackId, found, hooks);
        if (profile.trackId === trackId) renderTrackFeaturesBar(profileSource.getProfile());
      });
    }
  },
  function onError(err) {
    console.error('[radio] poll failed', err);
    elStatus.textContent = 'Connexion au Pi perdue…';
  }
);

// Re-renders the bottom bar's timing/upcoming-changes segments AND the
// lyrics banner every 0.5s, independent of the ~3s Sonos poll that drives
// onUpdate above - scene-manager.js already writes a fresh
// profile.estimatedPosition every render frame regardless, so this just
// needs to re-read it periodically. Both read the exact same
// currentPositionSeconds(profile) value on the same tick, so the lyrics
// banner's active line and the bar's own displayed clock can never drift
// apart from each other - one interval, one clock, two readouts. A
// dedicated small interval rather than hooking the render loop itself:
// this is a DOM text update, not something that needs to run at render
// framerate.
setInterval(function () {
  const profile = profileSource.getProfile();
  if (profile) {
    renderTrackFeaturesBar(profile);
    renderLyricsBanner(profile);
    renderMarksDebug(profile);
  }
}, 500);
// RADIO: le curseur de la ligne du morceau suit la lecture, image par image,
// quand le panneau technique est ouvert (comme play-boot.js).
requestAnimationFrame(function tic() {
  if (document.body.classList.contains('debug-active')) { const pr = profileSource.getProfile(); if (pr) panneauTechnique.tic(pr); }
  requestAnimationFrame(tic);
});


// ---------------------------------------------------------------------------
// RADIO: le tiroir - les deux seules fonctions de la page
//
// Au repos, l'image est seule : il ne reste qu'une poignée, qui bat doucement
// pour appeler le geste. Un glissement vers le haut (ou un clic) ouvre un
// panneau qui porte les DEUX fonctions, au même endroit, sous le même geste :
//   - CHOISIR UNE RADIO : une rangée de tuiles qui disent ce qu'elles jouent,
//     et l'on bascule SANS QUITTER LA PAGE (pas de rechargement, donc pas de
//     rupture de l'image ni du son) ;
//   - PROPOSER UN TITRE (la jam), qui n'apparaît que sur la radio personnelle,
//     puisqu'ailleurs la playlist ne nous appartient pas.
//
// Sur la télévision, ni poignée ni panneau : la télécommande a la grille de
// l'app pour les stations, et le bouton central pour la vignette QR.
// ---------------------------------------------------------------------------
const JAM_STATION_ID = 'judicael';
const JAM_POLL_MS = 15000;
const JAM_SEARCH_DEBOUNCE_MS = 260;
const SHEET_IDLE_MS = 3200;
const HINT_KEY = 'piradio.sheetHintSeen';

const JAM_TEXT = {
  fr: { fullOn: 'Plein écran', jam: 'Rejoins la jam', sub: 'Scanne pour choisir la musique', stations: 'Radios', findStation: 'Ajoute une radio', addTitle: 'Il vous manque une radio ?', pick: 'Ajoute un titre', addLong: 'Cherchez parmi {n} stations dans {c} pays', addShort: 'Ajouter une radio parmi {n}', addStation: 'Ajouter une radio', closeSearch: 'Fermer', searchScope: 'Chercher parmi {n} radios', dbScale: '{n} stations dans {c} pays', panel: 'Radios et jam', vizKeep: 'Garder l\u2019animation', vizAuto: 'Revenir au mode automatique', nextTrack: 'Titre suivant', myRadios: 'Mes radios', favourite: 'Mettre en favori', topIn: 'Top 20 {pays}', top: 'Top 20', searching: 'Recherche…', addStarting: 'Ajout en cours…', addReady: 'Prête ! Touche pour écouter', addWait: 'Encore {n} min avant ta prochaine radio', next: 'Ensuite', in: 'dans', search: 'Choisis le prochain titre', added: 'Ajouté à la file', wait: 'Encore {n} min avant ton prochain titre', full: 'File pleine, réessaie dans quelques minutes', error: 'Impossible pour l’instant', nothing: 'Aucun résultat', hint: 'Glisse pour changer de radio' },
  en: { fullOn: 'Full screen', jam: 'Join the jam', sub: 'Scan to pick the music', stations: 'Stations', findStation: 'Add a station', addTitle: 'Missing a station?', pick: 'Add a song', addLong: 'Search {n} stations across {c} countries', addShort: 'Add a station from {n}', addStation: 'Add a station', closeSearch: 'Close', searchScope: 'Search {n} stations', dbScale: '{n} stations in {c} countries', panel: 'Stations and jam', vizKeep: 'Keep the animation', vizAuto: 'Back to automatic', nextTrack: 'Next track', myRadios: 'My stations', favourite: 'Add to favourites', topIn: 'Top 20 {pays}', top: 'Top 20', searching: 'Searching…', addStarting: 'Adding…', addReady: 'Ready! Tap to listen', addWait: '{n} min before your next station', next: 'Next', in: 'in', search: 'Pick the next track', added: 'Added to the queue', wait: '{n} min before your next track', full: 'Queue is full, try again in a few minutes', error: 'Not possible right now', nothing: 'No results', hint: 'Swipe up to change station' },
  es: { fullOn: 'Pantalla completa', jam: 'Únete a la jam', sub: 'Escanea para elegir la música', stations: 'Emisoras', findStation: 'Añade una emisora', addTitle: '¿Falta una emisora?', pick: 'Añade una canción', addLong: 'Busca entre {n} emisoras en {c} países', addShort: 'Añade una emisora entre {n}', addStation: 'Añadir emisora', closeSearch: 'Cerrar', searchScope: 'Busca entre {n} emisoras', dbScale: '{n} emisoras en {c} países', panel: 'Emisoras y jam', vizKeep: 'Mantener la animación', vizAuto: 'Volver a automático', nextTrack: 'Próxima canción', myRadios: 'Mis emisoras', favourite: 'Añadir a favoritos', topIn: 'Top 20 {pays}', top: 'Top 20', searching: 'Buscando…', addStarting: 'Añadiendo…', addReady: '¡Lista! Toca para escuchar', addWait: '{n} min antes de tu próxima emisora', next: 'Después', in: 'en', search: 'Elige la próxima canción', added: 'Añadida a la cola', wait: '{n} min antes de tu próxima canción', full: 'Cola llena, inténtalo en unos minutos', error: 'Imposible ahora mismo', nothing: 'Sin resultados', hint: 'Desliza para cambiar de emisora' },
  de: { fullOn: 'Vollbild', jam: 'Mach mit bei der Jam', sub: 'Scannen und Musik wählen', stations: 'Sender', findStation: 'Sender hinzufügen', addTitle: 'Fehlt ein Sender?', pick: 'Titel hinzufügen', addLong: 'Suchen Sie unter {n} Sendern in {c} Ländern', addShort: 'Sender aus {n} hinzufügen', addStation: 'Sender hinzufügen', closeSearch: 'Schließen', searchScope: 'Unter {n} Sendern suchen', dbScale: '{n} Sender in {c} Ländern', panel: 'Sender und Jam', vizKeep: 'Animation beibehalten', vizAuto: 'Zurück zu automatisch', nextTrack: 'Nächster Titel', myRadios: 'Meine Sender', favourite: 'Zu Favoriten', topIn: 'Top 20 {pays}', top: 'Top 20', searching: 'Suche…', addStarting: 'Wird hinzugefügt…', addReady: 'Fertig! Zum Hören tippen', addWait: 'Noch {n} Min bis zum nächsten Sender', next: 'Als Nächstes', in: 'in', search: 'Wähl den nächsten Titel', added: 'Zur Warteschlange hinzugefügt', wait: 'Noch {n} Min bis zum nächsten Titel', full: 'Warteschlange voll, gleich nochmal', error: 'Gerade nicht möglich', nothing: 'Keine Treffer', hint: 'Nach oben wischen für Sender' },
  it: { fullOn: 'Schermo intero', jam: 'Unisciti alla jam', sub: 'Inquadra per scegliere la musica', stations: 'Radio', findStation: 'Aggiungi una radio', addTitle: 'Manca una radio?', pick: 'Aggiungi un brano', addLong: 'Cerca fra {n} radio in {c} paesi', addShort: 'Aggiungi una radio fra {n}', addStation: 'Aggiungi radio', closeSearch: 'Chiudi', searchScope: 'Cerca tra {n} radio', dbScale: '{n} radio in {c} paesi', panel: 'Radio e jam', vizKeep: 'Mantieni l\u2019animazione', vizAuto: 'Torna in automatico', nextTrack: 'Prossimo brano', myRadios: 'Le mie radio', favourite: 'Aggiungi ai preferiti', topIn: 'Top 20 {pays}', top: 'Top 20', searching: 'Ricerca…', addStarting: 'Aggiunta in corso…', addReady: 'Pronta! Tocca per ascoltare', addWait: 'Ancora {n} min prima della prossima radio', next: 'Dopo', in: 'tra', search: 'Scegli il prossimo brano', added: 'Aggiunto alla coda', wait: 'Ancora {n} min prima del prossimo brano', full: 'Coda piena, riprova tra poco', error: 'Non è possibile ora', nothing: 'Nessun risultato', hint: 'Scorri per cambiare radio' },
  pt: { fullOn: 'Ecrã inteiro', jam: 'Entra na jam', sub: 'Aponta para escolher a música', stations: 'Rádios', findStation: 'Adiciona uma rádio', addTitle: 'Falta uma rádio?', pick: 'Adiciona uma música', addLong: 'Procure entre {n} rádios em {c} países', addShort: 'Adiciona uma rádio entre {n}', addStation: 'Adicionar rádio', closeSearch: 'Fechar', searchScope: 'Procura entre {n} rádios', dbScale: '{n} rádios em {c} países', panel: 'Rádios e jam', vizKeep: 'Manter a animação', vizAuto: 'Voltar ao automático', nextTrack: 'Próxima música', myRadios: 'As minhas rádios', favourite: 'Adicionar aos favoritos', topIn: 'Top 20 {pays}', top: 'Top 20', searching: 'A procurar…', addStarting: 'A adicionar…', addReady: 'Pronta! Toca para ouvir', addWait: 'Faltam {n} min para a próxima rádio', next: 'A seguir', in: 'em', search: 'Escolhe a próxima música', added: 'Adicionada à fila', wait: 'Faltam {n} min para a próxima', full: 'Fila cheia, tenta daqui a pouco', error: 'Não é possível agora', nothing: 'Sem resultados', hint: 'Desliza para mudar de rádio' },
  nl: { fullOn: 'Volledig scherm', jam: 'Doe mee met de jam', sub: 'Scan om muziek te kiezen', stations: 'Zenders', findStation: 'Voeg een zender toe', addTitle: 'Mist u een zender?', pick: 'Nummer toevoegen', addLong: 'Zoek in {n} zenders uit {c} landen', addShort: 'Voeg een zender toe uit {n}', addStation: 'Zender toevoegen', closeSearch: 'Sluiten', searchScope: 'Zoek in {n} zenders', dbScale: '{n} zenders in {c} landen', panel: 'Zenders en jam', vizKeep: 'Animatie behouden', vizAuto: 'Terug naar automatisch', nextTrack: 'Volgend nummer', myRadios: 'Mijn zenders', favourite: 'Aan favorieten toevoegen', topIn: 'Top 20 {pays}', top: 'Top 20', searching: 'Zoeken…', addStarting: 'Toevoegen…', addReady: 'Klaar! Tik om te luisteren', addWait: 'Nog {n} min tot je volgende zender', next: 'Straks', in: 'over', search: 'Kies het volgende nummer', added: 'Toegevoegd aan de wachtrij', wait: 'Nog {n} min tot je volgende nummer', full: 'Wachtrij vol, probeer straks', error: 'Nu even niet mogelijk', nothing: 'Geen resultaten', hint: 'Veeg omhoog voor zenders' },
  pl: { fullOn: 'Pełny ekran', jam: 'Dołącz do jamu', sub: 'Zeskanuj i wybierz muzykę', stations: 'Stacje', findStation: 'Dodaj stację', addTitle: 'Brakuje stacji?', pick: 'Dodaj utwór', addLong: 'Szukaj wśród {n} stacji w {c} krajach', addShort: 'Dodaj stację spośród {n}', addStation: 'Dodaj stację', closeSearch: 'Zamknij', searchScope: 'Szukaj wśród {n} stacji', dbScale: '{n} stacji w {c} krajach', panel: 'Stacje i jam', vizKeep: 'Zachowaj animację', vizAuto: 'Wróć do trybu automatycznego', nextTrack: 'Następny utwór', myRadios: 'Moje stacje', favourite: 'Dodaj do ulubionych', topIn: 'Top 20 {pays}', top: 'Top 20', searching: 'Szukam…', addStarting: 'Dodawanie…', addReady: 'Gotowa! Dotknij, by słuchać', addWait: 'Jeszcze {n} min do następnej stacji', next: 'Następnie', in: 'za', search: 'Wybierz następny utwór', added: 'Dodano do kolejki', wait: 'Jeszcze {n} min do następnego utworu', full: 'Kolejka pełna, spróbuj za chwilę', error: 'Teraz nie można', nothing: 'Brak wyników', hint: 'Przesuń w górę, by zmienić stację' },
  ru: { fullOn: 'Полный экран', jam: 'Присоединяйся к джему', sub: 'Сканируй и выбирай музыку', stations: 'Станции', findStation: 'Добавь станцию', addTitle: 'Не хватает станции?', pick: 'Добавить трек', addLong: 'Поиск среди {n} станций в {c} странах', addShort: 'Добавить станцию из {n}', addStation: 'Добавить станцию', closeSearch: 'Закрыть', searchScope: 'Поиск среди {n} станций', dbScale: '{n} станций в {c} странах', panel: 'Станции и джем', vizKeep: 'Оставить анимацию', vizAuto: 'Вернуть автоматический режим', nextTrack: 'Следующий трек', myRadios: 'Мои станции', favourite: 'В избранное', topIn: 'Топ-20 {pays}', top: 'Топ-20', searching: 'Поиск…', addStarting: 'Добавление…', addReady: 'Готово! Нажми, чтобы слушать', addWait: 'Ещё {n} мин до следующей станции', next: 'Далее', in: 'через', search: 'Выбери следующий трек', added: 'Добавлено в очередь', wait: 'Ещё {n} мин до следующего трека', full: 'Очередь заполнена, попробуй позже', error: 'Сейчас недоступно', nothing: 'Ничего не найдено', hint: 'Проведите вверх, чтобы сменить станцию' },
  ja: { fullOn: '全画面', jam: 'ジャムに参加', sub: 'スキャンして曲を選ぼう', stations: '放送局', findStation: '放送局を追加', addTitle: 'お探しの放送局がない？', pick: '曲を追加', addLong: '{c}か国{n}局から検索', addShort: '{n}局から追加', addStation: '放送局を追加', closeSearch: '閉じる', searchScope: '{n}局から検索', dbScale: '{c}か国の{n}局', panel: '放送局とジャム', vizKeep: 'アニメーションを保つ', vizAuto: '自動に戻す', nextTrack: '次の曲', myRadios: 'マイ放送局', favourite: 'お気に入りに追加', topIn: '{pays}のトップ20', top: 'トップ20', searching: '検索中…', addStarting: '追加中…', addReady: '準備完了！タップで再生', addWait: '次の放送局まであと{n}分', next: '次は', in: 'あと', search: '次の曲を選ぼう', added: 'キューに追加しました', wait: '次の曲まであと{n}分', full: 'キューが一杯です', error: '今はできません', nothing: '結果なし', hint: '上にスワイプで放送局' },
  zh: { fullOn: '全屏', jam: '加入即兴合奏', sub: '扫码点歌', stations: '电台', findStation: '添加电台', addTitle: '没找到想听的电台？', pick: '添加歌曲', addLong: '在 {c} 个国家的 {n} 个电台中搜索', addShort: '从 {n} 个电台中添加', addStation: '添加电台', closeSearch: '关闭', searchScope: '在 {n} 个电台中搜索', dbScale: '{c} 个国家的 {n} 个电台', panel: '电台与合奏', vizKeep: '保持动画', vizAuto: '恢复自动', nextTrack: '下一首', myRadios: '我的电台', favourite: '加入收藏', topIn: '{pays}前20', top: '前20', searching: '搜索中…', addStarting: '正在添加…', addReady: '已就绪！点击收听', addWait: '还需 {n} 分钟才能再添加', next: '下一首', in: '还有', search: '选择下一首歌', added: '已加入队列', wait: '还需 {n} 分钟才能再点歌', full: '队列已满，请稍后再试', error: '暂时无法使用', nothing: '没有结果', hint: '上滑切换电台' },
  ko: { fullOn: '전체 화면', jam: '잼에 참여하기', sub: '스캔해서 음악 고르기', stations: '방송국', findStation: '방송국 추가', addTitle: '찾는 방송국이 없나요?', pick: '곡 추가', addLong: '{c}개국 {n}개 방송국에서 검색', addShort: '{n}개 중에서 추가', addStation: '방송국 추가', closeSearch: '닫기', searchScope: '{n}개 방송국에서 검색', dbScale: '{c}개국 {n}개 방송국', panel: '방송국과 잼', vizKeep: '애니메이션 유지', vizAuto: '자동으로 돌아가기', nextTrack: '다음 곡', myRadios: '내 방송국', favourite: '즐겨찾기에 추가', topIn: '{pays} 톱 20', top: '톱 20', searching: '검색 중…', addStarting: '추가 중…', addReady: '준비 완료! 눌러서 듣기', addWait: '다음 방송국까지 {n}분', next: '다음', in: '후', search: '다음 곡을 골라줘', added: '대기열에 추가됨', wait: '다음 곡까지 {n}분', full: '대기열이 가득 찼습니다', error: '지금은 불가능합니다', nothing: '결과 없음', hint: '위로 밀어 방송국 선택' },
  ar: { fullOn: 'ملء الشاشة', jam: 'انضم إلى الجلسة', sub: 'امسح لاختيار الموسيقى', stations: 'المحطات', findStation: 'أضف محطة', addTitle: 'لم تجد محطتك؟', pick: 'أضف أغنية', addLong: 'ابحث بين {n} محطة في {c} دولة', addShort: 'أضف محطة من {n}', addStation: 'أضف محطة', closeSearch: 'إغلاق', searchScope: 'ابحث بين {n} محطة', dbScale: '{n} محطة في {c} دولة', panel: 'المحطات والجلسة', vizKeep: 'أبقِ الرسوم المتحركة', vizAuto: 'العودة إلى الوضع التلقائي', nextTrack: 'الأغنية التالية', myRadios: 'محطاتي', favourite: 'أضف إلى المفضلة', topIn: 'أفضل 20 في {pays}', top: 'أفضل 20', searching: 'جارٍ البحث…', addStarting: 'جارٍ الإضافة…', addReady: 'جاهزة! اضغط للاستماع', addWait: 'بقي {n} دقيقة قبل محطتك التالية', next: 'التالي', in: 'خلال', search: 'اختر الأغنية التالية', added: 'أُضيفت إلى القائمة', wait: 'بقي {n} دقيقة قبل أغنيتك التالية', full: 'القائمة ممتلئة، حاول لاحقًا', error: 'غير ممكن الآن', nothing: 'لا نتائج', hint: 'اسحب للأعلى لتغيير المحطة' }
};
const JAM_COUNTRY_LANG = {
  FR: 'fr', BE: 'fr', MC: 'fr', LU: 'fr', CH: 'fr', CI: 'fr', SN: 'fr', MA: 'fr', TN: 'fr', DZ: 'fr', CM: 'fr', CD: 'fr', HT: 'fr', MG: 'fr',
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es', UY: 'es', EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', PY: 'es', CR: 'es', PA: 'es',
  DE: 'de', AT: 'de', IT: 'it', PT: 'pt', BR: 'pt', AO: 'pt', MZ: 'pt', NL: 'nl', PL: 'pl',
  RU: 'ru', BY: 'ru', KZ: 'ru', JP: 'ja', CN: 'zh', TW: 'zh', HK: 'zh', KR: 'ko',
  SA: 'ar', AE: 'ar', EG: 'ar', QA: 'ar', KW: 'ar'
};

const sheetEl = document.getElementById('sheet');
const sheetBodyEl = document.getElementById('sheet-body');
// LA LANGUE DU NAVIGATEUR D'ABORD, le pays de l'adresse ensuite.
//
// Le réglage du navigateur est une préférence que quelqu'un a formulée ; le
// pays de l'adresse n'est qu'une déduction. Un Français en voyage, un
// anglophone vivant à Paris, un appareil emprunté : dans tous ces cas la
// personne a déjà dit dans quelle langue elle veut lire, et il serait présomp-
// tueux de la contredire au vu de son adresse IP.
//
// On lit la liste complète des langues demandées, dans son ordre de
// préférence, et on prend la première qu'on sait parler. À défaut le pays
// tranche, et à défaut l'anglais.
const jamLang = (function () {
  const wanted = (navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || '']);
  for (let i = 0; i < wanted.length; i++) {
    const code = String(wanted[i] || '').slice(0, 2).toLowerCase();
    if (JAM_TEXT[code]) return code;
  }
  const country = typeof window !== 'undefined' ? window.__VIEWER_COUNTRY__ : null;
  const byCountry = country ? JAM_COUNTRY_LANG[country] : null;
  return JAM_TEXT[byCountry] ? byCountry : 'en';
})();
const jamWords = JAM_TEXT[jamLang];

let jamNextTrack = null;
let jamFetchedAt = 0;
let jamSearchTimer = null;
let jamSearchSeq = 0;
let jamFeedbackTimer = null;
let stationsNow = {};
let stationsNowAt = 0;
let idleTimer = null;
let radioPlayerStarted = !STATION_FROM_PATH;

if (sheetEl) {
  // « Titre suivant », pas « Rejoins la jam ». L'intitulé annonce ce qui est
  // écrit en dessous - le morceau qui arrive - et non ce qu'on vient faire là.
  // Sur téléphone c'est déjà #jam-card-name qui le dit, cet élément-ci y étant
  // masqué. L'invitation, elle, garde tout son sens sur la vignette QR de la
  // télévision, où il faut bien dire au visiteur pourquoi scanner.
  document.getElementById('jam-title').textContent = jamWords.nextTrack;
  document.getElementById('jam-qr-title').textContent = jamWords.jam;
  document.getElementById('jam-qr-sub').textContent = jamWords.sub;
  document.getElementById('jam-input').placeholder = jamWords.search;
  document.getElementById('jam-card-name').textContent = jamWords.nextTrack;
  const stationFilterEl = document.getElementById('station-filter');
  if (stationFilterEl) stationFilterEl.placeholder = jamWords.findStation;
  // Le champ dit ce qu'il fouille - « Chercher parmi 51 000 radios » vaut
  // toutes les icônes du monde -, et le bouton dit ce qu'il fait.
  const stationFilterEl2 = document.getElementById('station-filter');
  if (stationFilterEl2) stationFilterEl2.placeholder = searchScopeText();
  setSearchToggleLabel(false);
  // Ces trois-là restaient en français pour le monde entier : ils vivaient
  // dans le HTML et personne ne les remplaçait. Celui du verrou n'était réécrit
  // que par applyExtTrackMode, qui ne s'exécute pas sur une page ouverte sans
  // station ni changement d'état - donc, le plus souvent, jamais.
  const handleEl = document.getElementById('sheet-handle');
  if (handleEl) handleEl.setAttribute('aria-label', jamWords.panel);
  if (sheetBodyEl) sheetBodyEl.setAttribute('aria-label', jamWords.panel);

  if (jamLang === 'ar') sheetEl.setAttribute('dir', 'rtl');
}

// La boîte qui défilerait sous ce doigt-là : la première, en remontant depuis
// l'élément touché, qui a de quoi défiler dans un sens ou dans l'autre.
function scrollableUnder(node) {
  for (let el = node; el && el !== document.body; el = el.parentElement) {
    if (el.nodeType !== 1) continue;
    const st = window.getComputedStyle(el);
    if (/(auto|scroll)/.test(st.overflowY) && el.scrollHeight > el.clientHeight + 1) return el;
    if (/(auto|scroll)/.test(st.overflowX) && el.scrollWidth > el.clientWidth + 1) return el;
  }
  return null;
}

function jamApplies() {
  return !externalRadio.active || externalRadio.station.id === JAM_STATION_ID;
}

function refreshSheetVisibility() {
  // Les classes d'état ne portent JAMAIS le nom d'un élément : `body.jam`
  // serait aussi ciblé par une règle `.jam`, et masquerait le corps de page.
  document.body.classList.toggle('jam-on', jamApplies());
  document.body.classList.toggle('sheet-on', !TV_MODE || true);
  renderStations();
}

// --- les radios -------------------------------------------------------------

// Le rang vient du serveur (services/stationPicker.js) : il mêle les écoutes
// du pays du visiteur, les écoutes mondiales et le pays de la station. Le
// calculer ici reviendrait à ne connaître que ce que cette page voit.
const STATION_RANK = window.__STATION_RANK__ || {};

function stationList() {
  return Object.keys(STATIONS).map(function (id) { return STATIONS[id]; })
    .sort(function (a, b) {
      // La radio personnelle est toujours la première : c'est la maison, pas
      // une station parmi d'autres, et sa place ne dépend pas de l'audience.
      if (a.id !== b.id) {
        if (a.id === JAM_STATION_ID) return -1;
        if (b.id === JAM_STATION_ID) return 1;
      }
      const ra = STATION_RANK[a.id] || 0;
      const rb = STATION_RANK[b.id] || 0;
      if (ra !== rb) return rb - ra;
      return String(a.name).localeCompare(String(b.name), 'fr', { sensitivity: 'base' });
    });
}

// Ce que la recherche a rapporté. `base` sont des stations installées, qu'on
// peut écouter tout de suite ; `online` viennent de la base publique et ne
// sont pas encore chez nous - elles portent un bouton d'ajout.
const stationSearch = { q: '', base: [], online: [], seq: 0, pending: false };

function stationFilterText() {
  const el = document.getElementById('station-filter');
  return el ? el.value.trim() : '';
}

// La recherche de radios se replie derrière un « + » : elle sert rarement,
// dix-neuf stations étant déjà à l'écran, et un second champ en permanence se
// confondait avec celui de la jam.
// Le nombre de radios atteignables, mis en forme pour un humain : 51 608
// s'écrit avec une espace, et s'arrondit au millier parce que la précision
// n'apporte rien et vieillit mal.
function radioDbCount() {
  const db = window.__RADIO_DB__;
  if (!db || !db.stations) return null;
  const milliers = Math.floor(db.stations / 1000);
  return { n: milliers ? (milliers + ' 000') : String(db.stations), c: db.countries || 0 };
}

function searchScopeText() {
  const db = radioDbCount();
  if (!db) return jamWords.findStation;
  return jamWords.searchScope.replace('{n}', db.n);
}

function setSearchToggleLabel(open) {
  const btn = document.getElementById('station-search-toggle');
  const lab = document.getElementById('station-search-label');
  if (!btn) return;
  const db = radioDbCount();
  if (lab) {
    if (open) {
      lab.textContent = jamWords.closeSearch;
    } else if (db) {
      // Une accroche, puis le chiffre qui la justifie. La seconde ligne ne
      // paraît que sur écran large (voir .add-sub dans radio.html).
      lab.innerHTML = '<span class="add-title">' + jamEscape(jamWords.addTitle) + '</span>'
        + '<span class="add-title-short">'
        + jamEscape(jamWords.addShort.replace('{n}', db.n)) + '</span>'
        + '<span class="add-sub">'
        + jamEscape(jamWords.addLong.replace('{n}', db.n).replace('{c}', db.c)) + '</span>';
    } else {
      lab.textContent = jamWords.addStation;
    }
  }
  const plain = open ? jamWords.closeSearch : jamWords.addStation;
  btn.setAttribute('aria-label', plain);
  btn.setAttribute('title', plain);
  btn.setAttribute('aria-expanded', open ? 'true' : 'false');
}

function toggleStationSearch(open) {
  const el = document.getElementById('station-filter');
  if (!el) return;
  const want = typeof open === 'boolean' ? open : el.hidden;
  el.hidden = !want;
  document.body.classList.toggle('station-search-open', want);
  setSearchToggleLabel(want);
  // L'ampleur de la base se dit une fois le champ ouvert, là où elle sert.
  // L'ampleur se dit UNE fois, dans le champ lui-même. La répéter en dessous
  // faisait deux chiffres pour une seule idée.
  const scale = document.getElementById('station-scale');
  if (scale) scale.hidden = true;
  placeSearchToggle();
  syncJamHead();
  if (want) {
    // PAS de focus sur un écran tactile : il ferait surgir le clavier par-dessus
    // la moitié de l'écran, alors qu'on vient d'abord regarder les pays et les
    // radios proposées. Le clavier ne doit venir que si l'on touche le champ.
    // Au pointeur fin, rien ne masque quoi que ce soit et taper tout de suite
    // est un gain.
    const tactile = typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: coarse)').matches;
    if (!tactile) el.focus();
    // Les rangées s'effacent tout de suite : c'est l'ouverture qui fait place
    // nette, pas la première lettre. Et la fenêtre s'ouvre PLEINE - les pays,
    // les genres, et déjà des radios - au lieu d'un champ vide qui attend.
    renderStations();
    if (!browseState.countries.length) loadBrowse(null, null);
    else renderFacets();
  } else if (el.value) {
    el.value = '';
    onStationFilterInput();
  } else {
    renderStations();
  }
}

// Le « + » vit sur la ligne du premier intitulé visible. Sans favori, cette
// ligne-là n'existe pas : le bouton y restait seul, calé à droite d'un vide,
// et s'offrait un étage pour rien - criant sur un téléphone, où chaque ligne
// du panneau se paie. Il descend alors sur la ligne du classement.
function placeSearchToggle() {
  const btn = document.getElementById('station-search-toggle');
  const addHome = document.getElementById('add-home');
  const filterRow = document.getElementById('filter-row');
  if (!btn || !addHome || !filterRow) return;
  // Champ ouvert : le bouton va au bout du champ, où il devient la croix qui
  // referme. Fermé : sur la ligne des favoris s'ils ont un intitulé, sur celle
  // du classement sinon.
  // Le bouton a deux places, et deux seulement : au bout du champ quand il
  // est ouvert, où il devient la croix qui referme ; sur sa propre ligne
  // au-dessus de la grille sinon. L'intitulé « mes radios » a disparu - une
  // rangée de ses propres radios n'a pas besoin qu'on la nomme - et la ligne
  // ne porte donc plus que le bouton.
  // Fermé, le bouton vit SOUS la liste, sur toute la largeur : c'est la fin
  // de la liste qui pose la question « et si ma radio n'y est pas ? ».
  // Ouvert, il rejoint le bout du champ, où il devient la croix qui referme.
  const open = document.body.classList.contains('station-search-open');
  const host = open ? filterRow : addHome;
  if (btn.parentNode !== host) host.appendChild(btn);
}

// PARCOURIR : ce qu'on regarde quand le champ est ouvert mais vide. Chercher
// suppose de savoir quoi chercher ; parcourir ne le suppose pas.
const browseState = { country: null, tag: null, countries: [], tags: [], items: [], seq: 0 };

function countryName(code) {
  if (!code) return '';
  try {
    const dn = new Intl.DisplayNames([jamLang], { type: 'region' });
    return dn.of(code) || code;
  } catch (e) { return code; }
}

function loadBrowse(country, tag) {
  const seq = ++browseState.seq;
  const q = [];
  if (country) q.push('country=' + encodeURIComponent(country));
  if (tag) q.push('tag=' + encodeURIComponent(tag));
  fetch(API_BASE + '/api/stations/browse' + (q.length ? '?' + q.join('&') : ''))
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (d) {
      if (!d || seq !== browseState.seq) return;
      browseState.country = d.country;
      browseState.tag = d.tag;
      browseState.countries = d.countries || [];
      browseState.tags = d.tags || [];
      browseState.items = d.items || [];
      renderFacets();
      renderStations();
    })
    .catch(function () { /* la fenêtre garde ce qu'elle montrait */ });
}

function renderFacets() {
  const wrap = document.getElementById('station-facets');
  if (!wrap) return;
  // Les pastilles n'ont de sens que sur l'écran de parcours : dès qu'on tape,
  // c'est la frappe qui commande.
  const show = document.body.classList.contains('station-search-open') && !stationSearch.q;
  wrap.hidden = !show;
  if (!show) return;

  const pays = document.getElementById('facet-countries');
  const genres = document.getElementById('facet-tags');
  if (pays) {
    pays.innerHTML = browseState.countries.map(function (c) {
      return '<button type="button" class="facet' + (c.code === browseState.country ? ' on' : '') + '"'
        + ' data-country="' + jamEscape(c.code) + '">' + jamEscape(countryName(c.code)) + '</button>';
    }).join('');
    Array.prototype.forEach.call(pays.querySelectorAll('.facet'), function (b) {
      b.addEventListener('click', function () {
        const code = b.getAttribute('data-country');
        // Le genre retenu ne survit pas au changement de pays : il n'existe
        // pas forcément ailleurs.
        loadBrowse(code === browseState.country ? null : code, null);
      });
    });
  }
  // Pas de rangée de genres : un pays suffit à parcourir, et deux rangées de
  // pastilles au-dessus des radios repoussaient les radios hors de l'écran.
  if (genres) genres.hidden = true;
}

let stationSearchTimer = null;
function onStationFilterInput() {
  const q = stationFilterText();
  if (q === stationSearch.q) return;
  stationSearch.q = q;
  if (stationSearchTimer) clearTimeout(stationSearchTimer);
  if (!q) { stationSearch.base = []; stationSearch.online = []; renderStations(); return; }
  // Réponse au fil de la frappe, sans inonder le serveur ni la base publique.
  stationSearchTimer = setTimeout(runStationSearch, 260);
  renderStations();
}

function runStationSearch() {
  const q = stationSearch.q;
  if (!q) return;
  const seq = ++stationSearch.seq;
  stationSearch.pending = true;
  renderStations();
  fetch(API_BASE + '/api/stations/search?q=' + encodeURIComponent(q))
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (d) {
      // Une réponse en retard ne doit pas écraser une frappe plus récente.
      if (seq !== stationSearch.seq) return;
      stationSearch.pending = false;
      stationSearch.base = (d && d.base) || [];
      stationSearch.online = (d && d.online) || [];
      // Une station trouvée dans la base est jouable tout de suite : on la
      // range avec les autres pour que le clic la lance sans détour.
      stationSearch.base.forEach(function (st) { if (!STATIONS[st.id]) STATIONS[st.id] = st; });
      renderStations();
      fetchStationsNow(true);
    })
    .catch(function () {
      if (seq !== stationSearch.seq) return;
      stationSearch.pending = false;
      renderStations();
    });
}

function currentStationId() {
  return externalRadio.active ? externalRadio.station.id : JAM_STATION_ID;
}

// Les radios mises de côté par cet auditeur. Le serveur les reconnaît par un
// jeton de cookie, avec son adresse en filet (services/favouritesService.js).
const FAVOURITES = Array.isArray(window.__FAVOURITES__) ? window.__FAVOURITES__.slice() : [];

function isFavourite(id) { return FAVOURITES.indexOf(id) >= 0; }

// Le logo d'une station ajoutée peut être une adresse distante, quand son
// détourage a échoué : on la laisse telle quelle au lieu de la préfixer.
function stationArtUrl(art) {
  const a = String(art || '');
  return /^(https?:)?\/\//.test(a) ? a : API_BASE + '/' + a.replace(/^\//, '');
}

// Une tuile de station installée : on clique, elle joue. L'étoile en haut à
// droite la met de côté, et ce clic-là ne doit PAS lancer la radio.
function stationTile(st, current) {
  const accent = st.accent || '#8ea6c8';
  const fav = isFavourite(st.id);
  // La radio personnelle n'a pas d'étoile : elle est déjà première, toujours,
  // et la marquer d'un favori la ferait figurer deux fois dans la rangée.
  const heart = st.id === JAM_STATION_ID ? ''
    : '<span class="station-fav' + (fav ? ' on' : '') + '" role="button" tabindex="0"'
      + ' data-fav="' + jamEscape(st.id) + '" aria-label="' + jamEscape(jamWords.favourite) + '"'
      + ' aria-pressed="' + (fav ? 'true' : 'false') + '">' + (fav ? '\u2605' : '\u2606') + '</span>';
  return '<button type="button" class="station' + (st.id === current ? ' current' : '') + '"'
    + ' data-id="' + jamEscape(st.id) + '"'
    + ' style="--station-accent:' + jamEscape(accent) + ';--station-glow:' + jamEscape(accent) + '55">'
    + '<span class="station-tile"><img src="' + jamEscape(stationArtUrl(st.art)) + '" alt="' + jamEscape(st.name) + '"></span>'
    + heart
    + '<span class="station-now" id="station-now-' + jamEscape(st.id) + '">' + jamEscape(st.name) + '</span>'
    + '</button>';
}

// Le logo que la base publique donne avec une station. C'est une adresse
// étrangère : elle passe par le relais du Pi, qui vérifie que ce qu'il ramène
// est bien une image - et qui, servi en https, ne pourrait de toute façon pas
// charger directement une favicon annoncée en http. Le relais n'accepte le
// distant qu'en https : une favicon en http n'a pas de logo, et garde son « + ».
// Un logo de Radio Browser sur dix est annoncé en http. La page est en https
// et ne peut pas les charger elle-même, mais le relais les rapatrie depuis le
// serveur : ce qui arrive au navigateur est en https dans tous les cas. Les
// refuser ici ne protégeait rien et perdait un dixième des logos.
function candidateArtUrl(favicon) {
  const url = String(favicon || '');
  if (!/^https?:\/\//i.test(url)) return '';
  return API_BASE + '/api/art-proxy?url=' + encodeURIComponent(url);
}

// Une tuile de station TROUVÉE MAIS PAS ENCORE INSTALLÉE : dessin distinct et
// un signe d'ajout. On clique, elle s'installe. Son logo est celui de la base
// publique, quand elle en donne un - on reconnaît une radio à son logo bien
// avant de lire son nom ; le « + » se retire alors dans un coin.
function candidateTile(item) {
  // Pays et codec, sans le débit : un chiffre de plus sous chaque vignette ne
  // dit rien à qui choisit une radio, et il y en avait trente à l'écran.
  const sub = [item.country, item.codec].filter(Boolean).join(' · ');
  const art = candidateArtUrl(item.favicon);
  return '<button type="button" class="station station-new" data-uuid="' + jamEscape(item.uuid) + '"'
    + ' data-name="' + jamEscape(item.name) + '">'
    + '<span class="station-tile station-tile-new' + (art ? ' has-art' : '') + '">'
    + (art ? '<img src="' + jamEscape(art) + '" alt="">' : '')
    + '<span class="station-plus">+</span></span>'
    + '<span class="station-now"><b>' + jamEscape(item.name) + '</b><span>' + jamEscape(sub) + '</span></span>'
    + '</button>';
}

// Le nom du pays dans la langue de l'auditeur. Sans pays connu, l'étiquette
// tombe sur « Top 20 » tout court plutôt que d'inventer une origine.
function countryLabel() {
  const cc = window.__VIEWER_COUNTRY__;
  if (!cc) return null;
  try {
    const dn = new Intl.DisplayNames([jamLang], { type: 'region' });
    return dn.of(cc) || cc;
  } catch (e) { return cc; }
}

// Remplit une rangée. La signature évite de tout reconstruire à chaque
// rafraîchissement, mais dit aussi l'ORDRE et l'état des cœurs : les deux
// changent, et la rangée doit suivre.
function fillRow(row, list, online, current) {
  if (!row) return;
  const signature = list.map(function (st) { return st.id + (isFavourite(st.id) ? '*' : ''); }).join(',')
    + '|' + online.map(function (o) { return o.uuid; }).join(',');
  if (row.dataset.signature !== signature) {
    row.innerHTML = list.map(function (st) { return stationTile(st, current); }).join('')
      + online.map(candidateTile).join('');
    row.dataset.signature = signature;
    Array.prototype.forEach.call(row.querySelectorAll('.station[data-id]'), function (btn) {
      btn.addEventListener('click', function () { selectStation(btn.getAttribute('data-id')); });
    });
    Array.prototype.forEach.call(row.querySelectorAll('.station-fav'), function (heart) {
      const act = function (e) {
        // L'étoile vit DANS le bouton de la station : sans cela, le mettre en
        // favori lancerait aussi la radio.
        e.stopPropagation();
        e.preventDefault();
        toggleFavourite(heart.getAttribute('data-fav'));
      };
      heart.addEventListener('click', act);
      heart.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') act(e); });
    });
    // Un logo qui manque ne condamne pas la tuile pour la durée de la page.
    // Une image absente à l'instant du chargement - le logo d'une station
    // qu'on vient d'ajouter, ou un 404 mis en cache - revient un peu plus
    // tard. On lui laisse deux chances avant de renoncer.
    Array.prototype.forEach.call(row.querySelectorAll('.station[data-id] img'), function (img) {
      img.addEventListener('error', function () {
        const tries = Number(img.dataset.tries || 0);
        if (tries >= 2) { img.style.visibility = 'hidden'; return; }
        img.dataset.tries = String(tries + 1);
        const base = img.src.split('#')[0].replace(/[?&]r=\d+/, '');
        setTimeout(function () {
          img.style.visibility = '';
          img.src = base + (base.indexOf('?') < 0 ? '?' : '&') + 'r=' + Date.now();
        }, 2000 * (tries + 1));
      });
    });
    // Le logo d'un résultat de la base publique vient d'ailleurs : s'il ne
    // vient pas, on ne le redemande pas - la tuile retrouve son « + » entier.
    Array.prototype.forEach.call(row.querySelectorAll('.station-new img'), function (img) {
      img.addEventListener('error', function () {
        if (img.parentNode) img.parentNode.classList.remove('has-art');
        if (img.remove) img.remove();
      });
    });
    Array.prototype.forEach.call(row.querySelectorAll('.station-new'), function (btn) {
      btn.addEventListener('click', function () {
        startStationAdd(btn.getAttribute('data-uuid'), btn.getAttribute('data-name'));
      });
    });
  }
  Array.prototype.forEach.call(row.querySelectorAll('.station[data-id]'), function (btn) {
    btn.classList.toggle('current', btn.getAttribute('data-id') === current);
  });
}

function renderStations() {
  const row = document.getElementById('station-row');
  const rowFav = document.getElementById('station-row-fav');
  const label = document.getElementById('station-top-label');
  if (!row) return;
  const current = currentStationId();
  // Chercher commence au clic sur « + », pas à la première lettre. Laisser les
  // rangées en place jusqu'à la saisie faisait grandir le panneau au moment
  // précis où l'on venait chercher de la place - et sur un téléphone, poussait
  // le champ hors de l'écran.
  const searchOpen = document.body.classList.contains('station-search-open');
  const searching = searchOpen || !!stationSearch.q;

  // Pendant une recherche, tout l'espace revient aux résultats : les rangées
  // du haut disparaissent plutôt que de repousser ce qu'on est venu chercher.
  if (rowFav) rowFav.hidden = searching;
  const topHead = document.getElementById('top-head');
  if (topHead) topHead.hidden = searching;

  const favLabel = document.getElementById('fav-label');
  let shown;
  if (searchOpen && !stationSearch.q) {
    // Écran de parcours : aucune station installée à montrer, seulement ce que
    // la base publique propose pour le pays et le genre retenus.
    shown = [];
    fillRow(row, [], browseState.items, current);
    if (favLabel) favLabel.textContent = '';
    renderFacets();
  } else if (searching) {
    shown = stationSearch.base;
    fillRow(row, shown, stationSearch.online, current);
    if (favLabel) favLabel.textContent = '';
    renderFacets();
  } else {
    // La maison a quitté la rangée : elle a sa carte, en tête, avec sa jam.
    // Restent les favoris, puis le classement du pays.
    const favs = FAVOURITES
      .filter(function (id) { return id !== JAM_STATION_ID; })
      .map(function (id) { return STATIONS[id]; })
      .filter(Boolean);
    const top = stationList().filter(function (st) {
      return st.id !== JAM_STATION_ID && !isFavourite(st.id);
    });
    // Sans favori, l'intitulé n'a rien à annoncer et disparaît avec sa rangée.
    if (favLabel) favLabel.textContent = '';
    if (rowFav) rowFav.hidden = !favs.length;
    // Sans favori, il n'y a rien à séparer : le classement commence la grille.
    const sep = document.getElementById('top-head');
    if (sep) sep.hidden = !favs.length;
    // Avec des favoris, le classement se contente d'UNE rangée : les deux
    // blocs additionnés doivent tenir dans la hauteur qu'occupait le seul
    // classement, sinon le panneau grandit le jour où l'on met une étoile.
    document.body.classList.toggle('has-favourites', favs.length > 0);
    fillRow(rowFav, favs, [], current);
    // L'intitulé ne nomme plus le pays : ce n'est pas un classement national
    // mais LE classement, dont les radios du pays de l'auditeur occupent
    // simplement la tête (voir services/stationPicker.js).
    if (label) label.textContent = jamWords.top;
    fillRow(row, top, [], current);
    shown = favs.concat(top);
    // Sans cet appel, les pastilles de pays restaient sur l'écran d'accueil
    // après la fermeture de la recherche : plus rien ne les effaçait.
    renderFacets();
  }
  placeSearchToggle();
  renderHomeCard();

  const none = document.getElementById('station-none');
  if (none) {
    // « Aucun résultat » ne se dit que si l'on a cherché quelque chose. Un
    // champ qui vient de s'ouvrir n'a rien trouvé : il attend.
    const empty = !!stationSearch.q && !stationSearch.base.length && !stationSearch.online.length;
    none.hidden = !empty;
    none.textContent = stationSearch.pending ? jamWords.searching : jamWords.nothing;
  }

  shown.forEach(function (st) {
    const el = document.getElementById('station-now-' + st.id);
    if (!el) return;
    const info = stationsNow[st.id];
    const track = info && info.track;
    // Deux lignes séparées, titre puis artiste : chacune est coupée pour
    // elle-même (voir .station-now dans radio.html).
    if (track && track.title) {
      el.innerHTML = '<b>' + jamEscape(track.title) + '</b>'
        + '<span>' + jamEscape(track.artist || '') + '</span>';
    } else if (info && info.programme && info.programme.title) {
      el.innerHTML = '<b>' + jamEscape(st.name) + '</b>'
        + '<span>' + jamEscape(info.programme.title) + '</span>';
    } else {
      el.innerHTML = '<b>' + jamEscape(st.name) + '</b>';
    }
  });
}

// La carte de la maison : son logo, son nom, et ce qu'elle joue. Un clic la
// lance - ou y revient, quand on écoutait ailleurs. La jam, juste en dessous,
// appartient à la même carte (voir .home-card dans radio.html).
function renderHomeCard() {
  const st = STATIONS[JAM_STATION_ID];
  if (!st) return;
  const name = document.getElementById('home-name');
  if (name) name.textContent = st.name;
  const now = document.getElementById('home-now');
  const info = stationsNow[JAM_STATION_ID];
  const track = info && info.track;

  // La POCHETTE du morceau plutôt que le logo. judicael.com n'est pas une
  // marque qu'il faut reconnaître au milieu d'autres : c'est la maison, et ce
  // qu'elle joue en dit plus long sur elle qu'un sigle inventé. Le logo ne
  // revient qu'entre deux morceaux, faute de mieux.
  const img = document.getElementById('home-art-img');
  if (img) {
    const cover = track && track.image ? track.image : null;
    const wanted = cover || stationArtUrl(st.art);
    if (img.getAttribute('data-src') !== wanted) {
      img.setAttribute('data-src', wanted);
      img.src = wanted;
      img.alt = st.name;
    }
    // Une pochette remplit son cadre, un logo doit y tenir en entier.
    img.classList.toggle('is-cover', !!cover);
  }

  if (!now) return;
  if (track && track.title) {
    // Le même nettoyage que partout ailleurs sur cette page : rien entre
    // parenthèses, rien après un tiret ou une virgule. « Enjoy the Flight
    // (feat. Fréd) · Just Chillax, Fréd » n'apprend rien de plus que « Enjoy
    // the Flight · Just Chillax », et tient sur la ligne.
    now.innerHTML = jamEscape(cleanTrackName(track.title))
      + (track.artist ? ' <span class="artist">· ' + jamEscape(cleanTrackName(track.artist)) + '</span>' : '');
  } else {
    now.textContent = '';
  }
}

// Mettre de côté, ou retirer. L'affichage bouge tout de suite - la station
// change de rangée sous le doigt - et le serveur suit ; s'il refuse, on
// revient en arrière.
function toggleFavourite(id) {
  if (!id || !STATIONS[id]) return;
  const was = isFavourite(id);
  if (was) FAVOURITES.splice(FAVOURITES.indexOf(id), 1);
  else FAVOURITES.unshift(id);
  renderStations();
  fetch(API_BASE + '/api/favourites', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ station: id, on: !was })
  }).then(function (r) { return r.ok ? r.json() : null; })
    .then(function (d) {
      if (!d || !Array.isArray(d.stations)) return;
      FAVOURITES.length = 0;
      d.stations.forEach(function (x) { if (STATIONS[x]) FAVOURITES.push(x); });
      renderStations();
    })
    .catch(function () {
      // Le serveur n'a pas pris : la tuile retrouve son état d'avant.
      if (was && !isFavourite(id)) FAVOURITES.unshift(id);
      else if (!was && isFavourite(id)) FAVOURITES.splice(FAVOURITES.indexOf(id), 1);
      renderStations();
    });
}

// --- ajouter une radio -------------------------------------------------------
//
// L'auditeur clique, la radio démarre. C'est tout ce qu'il voit.
//
// Le serveur ne rend la main qu'une fois le flux vérifié et la station posée :
// à ce moment-là elle est jouable, et elle est déjà dans ses favoris. Le
// reste - trouver où la station publie son titre, fabriquer son vrai logo,
// en tirer sa couleur - prend une à deux minutes et se fait derrière, sans
// rien dire. Un bandeau qui raconterait ces étapes ferait attendre devant un
// travail dont personne n'a besoin d'attendre la fin.
//
// Seul un échec se montre : si le flux ne répond pas, la radio ne jouera pas,
// et ça il faut le dire.
let addPoll = null;

function renderAddBanner(state) {
  const el = document.getElementById('add-banner');
  if (!el) return;
  if (!state) { el.hidden = true; el.className = 'add-banner'; el.innerHTML = ''; el.onclick = null; return; }
  el.hidden = false;
  el.className = 'add-banner ' + (state.kind || 'working') + (state.onClick ? ' clickable' : '');
  el.innerHTML = '<span class="add-banner-name">' + jamEscape(state.name || '') + '</span>'
    + '<span class="add-banner-step">' + jamEscape(state.message || '') + '</span>';
  el.onclick = state.onClick || null;
}

function startStationAdd(uuid, name) {
  if (!uuid || addPoll) return;
  fetch(API_BASE + '/api/station/add', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uuid: uuid })
  }).then(function (r) {
    return r.json().then(function (body) { return { ok: r.ok, body: body }; });
  }).then(function (res) {
    const b = res.body || {};
    if (!res.ok || !b.station) {
      const msg = b.error === 'cooldown'
        ? jamWords.addWait.replace('{n}', Math.max(1, Math.ceil((b.waitSeconds || 60) / 60)))
        : (b.error || jamWords.error);
      renderAddBanner({ kind: 'failed', name: name, message: msg });
      setTimeout(function () { renderAddBanner(null); }, 8000);
      return;
    }
    const st = b.station;
    STATIONS[st.id] = st;
    STATION_RANK[st.id] = Number.MAX_SAFE_INTEGER;
    // Le serveur l'a déjà mise en favori ; on recopie sa liste plutôt que de
    // basculer l'étoile nous-mêmes, pour ne pas la remettre à zéro.
    if (Array.isArray(b.favourites)) {
      FAVOURITES.length = 0;
      b.favourites.forEach(function (x) { FAVOURITES.push(x); });
    } else if (FAVOURITES.indexOf(st.id) < 0) {
      FAVOURITES.unshift(st.id);
    }
    const filter = document.getElementById('station-filter');
    if (filter) filter.value = '';
    stationSearch.q = '';
    stationSearch.base = [];
    stationSearch.online = [];
    renderStations();
    quietRefine(b.jobId, st.id);
    selectStation(st.id);
  }).catch(function () {
    renderAddBanner({ kind: 'failed', name: name, message: jamWords.error });
    setTimeout(function () { renderAddBanner(null); }, 6000);
  });
}

// Le vrai logo et la couleur arrivent une minute plus tard. On les récupère
// sans un mot : la vignette change toute seule, ce qui est exactement ce
// qu'on veut - la nouveauté, c'est la radio, pas la machinerie.
function quietRefine(jobId, id) {
  if (!jobId || addPoll) return;
  let tries = 0;
  addPoll = setInterval(function () {
    if (++tries > 12) { clearInterval(addPoll); addPoll = null; return; }
    fetch(API_BASE + '/api/station/add/' + encodeURIComponent(jobId))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (st) {
        if (!st) return;
        if (st.station) {
          STATIONS[id] = st.station;
          if (externalRadio.active && externalRadio.station && externalRadio.station.id === id) {
            externalRadio.station = st.station;
          }
          renderStations();
        }
        if (st.state !== 'running') { clearInterval(addPoll); addPoll = null; }
      })
      .catch(function () { /* on réessaiera au prochain tour */ });
  }, 15000);
}

function fetchStationsNow(force) {
  if (!force && Date.now() - stationsNowAt < JAM_POLL_MS) return;
  stationsNowAt = Date.now();
  const ids = Object.keys(STATIONS).slice(0, 30).join(',');
  fetch(API_BASE + '/api/stations/now?ids=' + encodeURIComponent(ids))
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => { if (d && d.stations) { stationsNow = d.stations; renderStations(); } })
    .catch(() => { /* la rangée garde ce qu'elle affichait */ });
}

// Basculer SANS recharger la page : c'est ce qui préserve l'immersion.
function selectStation(id) {
  const st = STATIONS[id];
  if (!st) return;
  if (id === JAM_STATION_ID) {
    if (externalRadio.active) toggleExternalStation(externalRadio.station);
    if (!radioPlayerStarted) { radioPlayer.start(); radioPlayerStarted = true; }
  } else if (!externalRadio.active || externalRadio.station.id !== id) {
    toggleExternalStation(st);
  }
  refreshSheetVisibility();
  fetchJamQueue(true);
  closeSheet();
  setTimeout(sendListenBeacon, 2500);
}

// --- le titre suivant et la jam ---------------------------------------------

function fetchJamQueue(force) {
  if (!sheetEl || !jamApplies()) return;
  if (!force && Date.now() - jamFetchedAt < JAM_POLL_MS) return;
  jamFetchedAt = Date.now();
  fetch(API_BASE + '/api/soloist/queue?n=1')
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => { jamNextTrack = (d && d.items && d.items[0]) || null; renderJamNext(); })
    .catch(() => {});
}

// Sur téléphone, la carte prend la tête « titre suivant » - mais seulement
// s'il y a un titre à venir à y mettre, et tant qu'on ne cherche pas une
// Sinon la tête judicael.com revient. La bascule est dans radio.html, sous
// body.jam-head.
//
// La recherche de radio N'entre plus dans cette condition : la carte garde le
// même aspect qu'on cherche ou non, puisque la station, elle, joue toujours.
function syncJamHead() {
  document.body.classList.toggle('jam-head',
    jamApplies() && !!jamNextTrack);
}

// La pochette du titre à venir. La file de Soloist ne donne que le titre et
// l'artiste (`ctl queue` est du texte) : la pochette se demande à part, et une
// seule fois par titre - la clé évite de la redemander à chaque décompte.
let jamNextArtKey = null;
function renderJamArt() {
  const img = document.getElementById('jam-art-img');
  if (!img) return;
  if (!jamApplies() || !jamNextTrack) { img.removeAttribute('src'); jamNextArtKey = null; return; }
  const key = jamNextTrack.title + '|' + jamNextTrack.artist;
  if (key === jamNextArtKey) return;
  jamNextArtKey = key;
  img.removeAttribute('src');
  fetch(API_BASE + '/api/cover?title=' + encodeURIComponent(jamNextTrack.title || '')
    + '&artist=' + encodeURIComponent(jamNextTrack.artist || ''))
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      // Un autre titre a pu arriver pendant la requête : la pochette qui
      // revient n'est alors plus celle qu'on attend.
      if (jamNextArtKey !== key || !d || !d.image) return;
      img.src = d.image;
    })
    .catch(() => {});
}

function renderJamNext() {
  if (!sheetEl) return;
  const el = document.getElementById('jam-next');
  const timerEl = document.getElementById('jam-timer');
  const tv = document.getElementById('sheet-tvline');
  renderJamArt();
  syncJamHead();
  if (!jamApplies() || !jamNextTrack) {
    el.textContent = '';
    if (timerEl) timerEl.textContent = '';
    if (tv) tv.textContent = '';
    return;
  }
  const profile = profileSource.getProfile();
  let left = null;
  if (profile && profile.duration > 0) {
    left = Math.max(0, Math.round(profile.duration - currentPositionSeconds(profile)));
  }
  const titre = cleanTrackName(jamNextTrack.title);
  const artiste = jamNextTrack.artist ? cleanTrackName(jamNextTrack.artist) : '';
  const name = titre + (artiste ? ' · ' + artiste : '');
  // Sans le mot « Ensuite » : un titre suivi d'un décompte se lit tout seul.
  const countdown = left != null ? jamWords.in + ' ' + jamCountdown(left) : '';
  // Dans la carte, le décompte prend sa propre ligne : la place manque en
  // largeur, pas en hauteur, et le titre n'a plus à se faire couper pour lui
  // laisser la fin de la ligne.
  // L'artiste dans sa propre balise : sur téléphone il s'efface pour laisser
  // la place au bouton (voir .jam-next-artist dans radio.html).
  el.innerHTML = '<b>' + jamEscape(titre) + '</b>'
    + (artiste ? '<span class="jam-next-artist"> · ' + jamEscape(artiste) + '</span>' : '');
  if (timerEl) timerEl.textContent = countdown;
  // Sur la télévision il ne reste qu'une ligne : là, tout tient ensemble.
  if (tv) tv.innerHTML = '<b>' + jamEscape(name) + '</b>' + (countdown ? ' · ' + countdown : '');
}

// « Alkapone - Summer Edit », « La Mezcla (feat. Totó) - Long Edit », « Danit,
// HUGEL » : dans la ligne « Ensuite », personne n'a besoin de la mention de
// version ni de la liste complète des invités. On garde le nom nu - ce qui
// suit un tiret ou une virgule saute, comme ce qui tient entre parenthèses.
function cleanTrackName(text) {
  const raw = String(text || '').trim();
  const stripped = raw
    .replace(/\s*[\(\[][^\)\]]*[\)\]]/g, '')
    .split(/\s+[-–—]\s+/)[0]
    .split(',')[0]
    .trim();
  return stripped || raw;   // un nom entièrement parenthésé reste lisible
}

function jamEscape(text) {
  return String(text).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

function jamCountdown(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m + ':' + (s < 10 ? '0' : '') + s;
}

function jamFeedback(text, kind) {
  const el = document.getElementById('jam-feedback');
  if (!el) return;
  el.textContent = text;
  el.className = 'jam-feedback' + (kind ? ' ' + kind : '');
  clearTimeout(jamFeedbackTimer);
  if (text) jamFeedbackTimer = setTimeout(() => { el.textContent = ''; el.className = 'jam-feedback'; }, 6000);
}

// Les résultats prennent la place de la pochette plutôt que de s'ajouter à
// elle : sans ça, le panneau grandissait de leur hauteur à chaque frappe (voir
// body.jam-results-open dans radio.html).
function jamResults(html) {
  const results = document.getElementById('jam-results');
  if (!results) return null;
  results.innerHTML = html;
  document.body.classList.toggle('jam-results-open', !!html);
  return results;
}

function jamSearch(query) {
  const results = document.getElementById('jam-results');
  if (!query || query.trim().length < 2) { jamResults(''); return; }
  // Les réponses ne reviennent pas dans l'ordre où on les demande : sans ce
  // compteur, la recherche sur « st » écrase celle sur « stromae ».
  const seq = ++jamSearchSeq;
  fetch(API_BASE + '/api/jam/search?q=' + encodeURIComponent(query))
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('http'))))
    .then((d) => {
      if (seq !== jamSearchSeq) return;
      const items = (d && d.items) || [];
      if (!items.length) { jamResults(''); jamFeedback(jamWords.nothing, ''); return; }
      jamResults(items.map((t) => (
        '<button type="button" class="jam-result">'
        + (t.image ? '<img src="' + jamEscape(t.image) + '" alt="">' : '<img alt="">')
        + '<span><span class="t">' + jamEscape(t.title) + '</span>'
        + '<span class="a">' + jamEscape(t.artist) + '</span></span></button>'
      )).join(''));
      Array.prototype.forEach.call(results.querySelectorAll('.jam-result'), (btn, i) => {
        btn.addEventListener('click', () => jamAdd(items[i]));
      });
    })
    .catch(() => jamFeedback(jamWords.error, 'no'));
}

function jamAdd(track) {
  jamFeedback('…', '');
  fetch(API_BASE + '/api/jam/add', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uri: track.uri, title: track.title, artist: track.artist })
  }).then((r) => r.json())
    .then((body) => {
      if (body && body.ok) {
        jamFeedback(jamWords.added + ' · ' + track.title, 'ok');
        jamResults('');
        document.getElementById('jam-input').value = '';
        fetchJamQueue(true);
        return;
      }
      if (body && body.reason === 'cooldown') {
        jamFeedback(jamWords.wait.replace('{n}', Math.max(1, Math.ceil(body.waitSeconds / 60))), 'no');
      } else if (body && body.reason === 'full') {
        jamFeedback(jamWords.full, 'no');
      } else {
        jamFeedback((body && body.message) || jamWords.error, 'no');
      }
    })
    .catch(() => jamFeedback(jamWords.error, 'no'));
}

// --- ouvrir, fermer, s'effacer ----------------------------------------------

function openSheet() {
  if (!sheetEl || TV_MODE) return;
  const filter = document.getElementById('station-filter');
  if (filter) { filter.value = ''; filter.hidden = true; }
  document.body.classList.remove('station-search-open');
  document.body.classList.remove('jam-open');
  stationSearch.q = '';
  stationSearch.base = [];
  stationSearch.online = [];
  document.body.classList.add('sheet-open');
  hideHint();
  fetchStationsNow(true);
  fetchJamQueue(true);
  renderStations();
}

function closeSheet() {
  if (!sheetEl) return;
  document.body.classList.remove('sheet-open');
  const input = document.getElementById('jam-input');
  if (input) { input.value = ''; input.blur(); }
  jamResults('');
  jamFeedback('', '');
}

function sheetOpen() {
  return document.body.classList.contains('sheet-open');
}

// --- le plein écran ----------------------------------------------------------
//
// Sur un vidéoprojecteur ou une télé en HDMI, c'est ce qui fait disparaître
// entièrement l'habillage du navigateur. Le navigateur ne l'accorde que sur un
// geste de l'utilisateur - il n'y a aucun moyen de le demander au chargement -
// d'où le bouton, qui est la seule façon honnête de l'offrir.
//
// Et un plein écran qui s'éteint au bout de dix minutes ne sert à rien : tant
// qu'on y est, on demande aussi à l'écran de rester allumé. C'est invisible et
// sans réglage, parce que personne ne veut décider de ça au milieu d'une
// soirée. Les deux vont ensemble et s'arrêtent ensemble.

function fullscreenEnabled() {
  if (typeof document.fullscreenEnabled === 'boolean') return document.fullscreenEnabled;
  if (typeof document.webkitFullscreenEnabled === 'boolean') return document.webkitFullscreenEnabled;
  const el = document.documentElement;
  return !!(el.requestFullscreen || el.webkitRequestFullscreen);
}

function fullscreenNow() {
  return document.fullscreenElement || document.webkitFullscreenElement || null;
}

function enterFullscreen() {
  const el = document.documentElement;
  const go = el.requestFullscreen || el.webkitRequestFullscreen;
  if (!go) return Promise.resolve();
  try { return Promise.resolve(go.call(el)); } catch (e) { return Promise.resolve(); }
}

function leaveFullscreen() {
  const go = document.exitFullscreen || document.webkitExitFullscreen;
  if (!go) return Promise.resolve();
  try { return Promise.resolve(go.call(document)); } catch (e) { return Promise.resolve(); }
}

// L'écran allumé. Le navigateur relâche le verrou dès que la page passe en
// arrière-plan, et ne le rend pas tout seul au retour : il faut le redemander.
let screenLock = null;

function holdScreen(on) {
  if (!navigator.wakeLock || !navigator.wakeLock.request) return;
  if (!on) {
    if (screenLock) { try { screenLock.release(); } catch (e) { /* déjà relâché */ } }
    screenLock = null;
    return;
  }
  if (screenLock) return;
  navigator.wakeLock.request('screen').then(function (lock) {
    screenLock = lock;
    lock.addEventListener('release', function () { screenLock = null; });
  }, function () {
    // Refusé (batterie faible, réglage du système) : le plein écran marche
    // quand même, on ne dit rien.
  });
}

function syncFullscreen() {
  const on = !!fullscreenNow();
  document.body.classList.toggle('is-fullscreen', on);
  holdScreen(on);
}

function setupFullscreen() {
  const btn = document.getElementById('viz-full');
  if (!btn) return;
  // Sur la télévision on y est déjà ; sur iPhone, Safari ne sait le faire que
  // pour une vidéo. Dans les deux cas le bouton ne doit pas exister plutôt que
  // d'être là sans effet.
  if (TV_MODE || !fullscreenEnabled()) { btn.remove(); return; }
  btn.hidden = false;
  btn.setAttribute('aria-label', jamWords.fullOn);
  btn.addEventListener('click', function (e) {
    // Ce clic demande le plein écran et rien d'autre : ni lecture, ni pause.
    e.stopPropagation();
    if (fullscreenNow()) leaveFullscreen(); else enterFullscreen();
  });
  document.addEventListener('fullscreenchange', syncFullscreen);
  document.addEventListener('webkitfullscreenchange', syncFullscreen);
  // Revenu sur la page après un détour : le verrou d'écran a sauté, pas le
  // plein écran. On remet celui-là seul.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden && fullscreenNow()) holdScreen(true);
  });
}

setupFullscreen();

// La poignée s'efface quand plus rien ne bouge, et revient au moindre signe.
function wake() {
  document.body.classList.remove('controls-idle');
  clearTimeout(idleTimer);
  idleTimer = setTimeout(function () {
    if (!sheetOpen()) document.body.classList.add('controls-idle');
  }, SHEET_IDLE_MS);
}

function showHintOnce() {
  let seen = false;
  try { seen = window.localStorage.getItem(HINT_KEY) === '1'; } catch (e) { seen = false; }
  if (seen || TV_MODE) return;
  const hint = document.getElementById('sheet-hint');
  if (!hint) return;
  hint.textContent = jamWords.hint;
  hint.hidden = false;
  setTimeout(hideHint, 5000);
}

function hideHint() {
  const hint = document.getElementById('sheet-hint');
  if (!hint || hint.hidden) return;
  hint.hidden = true;
  try { window.localStorage.setItem(HINT_KEY, '1'); } catch (e) { /* tant pis */ }
}

if (sheetEl) {
  document.getElementById('sheet-handle').addEventListener('click', function (e) {
    e.stopPropagation();
    if (sheetOpen()) closeSheet(); else openSheet();
  });
  document.getElementById('sheet-scrim').addEventListener('click', closeSheet);

  const stationFilter = document.getElementById('station-filter');
  if (stationFilter) {
    stationFilter.addEventListener('input', onStationFilterInput);
    stationFilter.addEventListener('search', onStationFilterInput);
  }

  const searchToggle = document.getElementById('station-search-toggle');
  if (searchToggle) searchToggle.addEventListener('click', toggleStationSearch);

  // « Choisis » : déplie le champ de la jam, sur téléphone uniquement (le
  // bouton n'y est visible que là - voir .jam-pick dans radio.html).
  const jamPick = document.getElementById('jam-pick');
  if (jamPick) {
    jamPick.textContent = jamWords.pick;
    jamPick.addEventListener('click', function (e) {
      e.stopPropagation();
      document.body.classList.add('jam-open');
      const input = document.getElementById('jam-input');
      if (input) input.focus();
    });
  }

  const homePlay = document.getElementById('home-play');
  if (homePlay) homePlay.addEventListener('click', function () { selectStation(JAM_STATION_ID); });

  document.getElementById('jam-input').addEventListener('input', function (e) {
    clearTimeout(jamSearchTimer);
    const q = e.target.value;
    jamSearchTimer = setTimeout(() => jamSearch(q), JAM_SEARCH_DEBOUNCE_MS);
  });

  // Le geste : vers le haut on ouvre, vers le bas on referme. Il vaut sur la
  // poignée comme sur le panneau, pour que le doigt n'ait pas à viser.
  //
  // Mais le panneau est plein de choses qui défilent - la rangée des radios
  // en travers, la liste des résultats et le panneau lui-même vers le bas.
  // Un doigt qui fait défiler finit forcément plus bas ou plus haut qu'il n'a
  // commencé : sans les trois gardes ci-dessous, lâcher un défilement fermait
  // le panneau. On ne garde le geste que s'il est vertical, que rien n'a
  // défilé pendant, et - pour fermer - que le panneau est déjà en haut de sa
  // course : c'est là qu'un glissement vers le bas ne peut plus rien vouloir
  // dire d'autre.
  let touchY = null;
  let touchX = null;
  let scroller = null;
  let scrollFrom = [0, 0];
  sheetEl.addEventListener('touchstart', function (e) {
    const t = e.touches && e.touches[0];
    touchY = t ? t.clientY : null;
    touchX = t ? t.clientX : null;
    scroller = t ? scrollableUnder(e.target) : null;
    scrollFrom = scroller ? [scroller.scrollTop, scroller.scrollLeft] : [0, 0];
    wake();
  }, { passive: true });
  sheetEl.addEventListener('touchend', function (e) {
    if (touchY == null) return;
    const end = e.changedTouches && e.changedTouches[0];
    const dy = end ? end.clientY - touchY : 0;
    const dx = end ? end.clientX - touchX : 0;
    const scrolled = !!scroller
      && (scroller.scrollTop !== scrollFrom[0] || scroller.scrollLeft !== scrollFrom[1]);
    touchY = null;
    touchX = null;
    scroller = null;
    if (scrolled || Math.abs(dx) > Math.abs(dy)) return;
    if (dy < -30) openSheet();
    else if (dy > 30 && sheetOpen() && (!sheetBodyEl || sheetBodyEl.scrollTop <= 0)
      && document.activeElement !== document.getElementById('jam-input')) closeSheet();
  }, { passive: true });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sheetOpen()) closeSheet();
  });
  window.addEventListener('mousemove', wake, { passive: true });
  window.addEventListener('touchstart', wake, { passive: true });
}

// --- dire au Pi ce qu'on écoute --------------------------------------------
//
// Les stations externes sont lues depuis leurs propres CDN : sans ce signal,
// le Pi ne sait rien de leur audience. Un signal au début, puis un par minute
// tant que le son coule - c'est la page qui compte les écoutes.
const LISTEN_BEACON_MS = 60000;

function currentlyPlayingStationId() {
  if (externalRadio.active) {
    return externalRadio.paused ? null : externalRadio.station.id;
  }
  return radioPlayer && radioPlayer.playing ? JAM_STATION_ID : null;
}

function sendListenBeacon() {
  const id = currentlyPlayingStationId();
  if (!id) return;
  const body = JSON.stringify({ station: id });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(API_BASE + '/api/listen', new Blob([body], { type: 'application/json' }));
      return;
    }
  } catch (e) { /* on retombe sur fetch */ }
  fetch(API_BASE + '/api/listen', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true })
    .catch(() => {});
}

setInterval(sendListenBeacon, LISTEN_BEACON_MS);

// LE DIAGNOSTIC DE LATENCE, une fois par minute tant que le flux joue (voir
// /api/radio-diag) : ce que CE navigateur mesure, pour les cas où la page
// reste décalée sur le son.
function envoyerDiagLatence() {
  const a = document.getElementById('radio-audio');
  if (!a || a.paused || !radioPlayer || externalRadio.active) return;
  const r = radioPlayer.retardLecteurSecondes();
  const lat = latenceMesuree();
  const p = radioPlayer._premierSon;
  const pr = profileSource.getProfile();
  const body = JSON.stringify({
    retard: r, latence: LATENCE_FORCEE ? RADIO_LATENCY_S : (lat != null ? lat : RADIO_LATENCY_S), mesuree: !LATENCE_FORCEE && lat != null,
    ct: a.currentTime, rs: a.readyState, paused: a.paused, connexion: radioPlayer._connexionA,
    premierSon: p ? p.t : null, premierCt: p ? p.ct : null, maintenant: performance.now(), titre: pr ? pr.title : ''
  });
  fetch(API_BASE + '/api/radio-diag', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true }).catch(function () {});
}
setTimeout(envoyerDiagLatence, 20000);
setInterval(envoyerDiagLatence, 60000);
setTimeout(sendListenBeacon, 5000);

refreshSheetVisibility();
fetchStationsNow(true);
fetchJamQueue(true);
wake();
setTimeout(showHintOnce, 2500);
// Le décompte se lit à la seconde ; les files et les stations toutes les 15 s.
setInterval(function () {
  fetchJamQueue(false);
  renderJamNext();
  if (sheetOpen()) fetchStationsNow(false);
}, 1000);

// La vignette QR de la télévision, appelée par le bouton central.
function toggleJamQr() {
  if (!sheetEl || !jamApplies()) return;
  document.body.classList.toggle('jam-qr-open');
}

// RADIO: driven by a remote (?tv=1). Arrows are the hotkeys above; OK means
// "back to the station grid", which this page cannot do by itself - it is
// either a page of the TV app (navigate to it) or a frame inside it (ask).
// Messages the other way are accepted too, for a wrapper that kept focus.
if (TV_MODE) {
  const leave = function () {
    if (window.parent !== window) {
      try { window.parent.postMessage({ source: 'piradio-radio', type: 'home' }, '*'); return; } catch (err) { /* fall through */ }
    }
    location.href = 'index.html';
  };
  window.addEventListener('keydown', function (e) {
    const isEnter = e.key === 'Enter' || e.keyCode === 13;
    const isBack = e.key === 'Escape' || e.keyCode === 10009; // Tizen RETURN
    if (!isEnter && !isBack) return;
    // The press that started the sound must not also do anything else.
    if (isEnter && (externalRadio.blocked || externalRadio.justUnblocked)) return;
    e.preventDefault();
    // OK montre (et cache) la vignette QR de la jam sur la radio personnelle ;
    // partout ailleurs il ramène à la grille, comme RETURN.
    if (isEnter && jamApplies()) { toggleJamQr(); return; }
    leave();
  });
  // The TV's RETURN key arrives as a document event, not a keypress.
  document.addEventListener('tizenhwkey', function (e) {
    if (e.keyName === 'back') leave();
  });
  window.addEventListener('message', function (e) {
    const d = e.data;
    if (!d || d.source !== 'piradio-tv') return;
    if (d.type === 'viz') stepVisual(d.dir < 0 ? -1 : 1);
    else if (d.type === 'gesture') externalRadio.unblock();
  });
}

// RADIO: l'écran de connexion (radio.html, .connexion). Le moteur reste à
// l'arrêt tant que l'état différé du morceau n'est pas là (une douzaine de
// secondes après l'ouverture, le son arrivant vers six) : l'écran animé
// couvre ce temps, en trois états - connexion, son arrivé (égaliseur, ondes
// plus vives), geste demandé - puis s'efface en fondu dès que le titre est
// posé (overlay-attente retiré). Jamais sur une page de station
// (judicael.com/rfm) ; garde-fou à 30 s.
(function connexion() {
  const body = document.body;
  if (STATION_FROM_PATH) { body.classList.remove('connexion-on'); return; }
  const TXT = {
    fr: ['Connexion à la radio', 'Le son arrive', 'Touchez l\'écran pour lancer le son'],
    en: ['Tuning in', 'Here comes the sound', 'Tap anywhere to start the sound'],
    es: ['Conectando con la radio', 'Llega el sonido', 'Toca la pantalla para empezar'],
    de: ['Verbindung zum Radio', 'Der Ton kommt', 'Tippe, um den Ton zu starten'],
    it: ['Connessione alla radio', 'Arriva il suono', 'Tocca lo schermo per avviare l\'audio'],
    pt: ['Ligando à rádio', 'O som está a chegar', 'Toque no ecrã para iniciar o som'],
    nl: ['Verbinden met de radio', 'Het geluid komt eraan', 'Tik om het geluid te starten']
  };
  const t = TXT[jamLang] || TXT.en;
  const texte = document.getElementById('connexion-texte');
  const audio = document.getElementById('radio-audio');
  const statut = document.getElementById('radio-status');
  let fini = false;
  function ecrire(s) { if (texte && texte.textContent !== s) texte.textContent = s; }
  function geste() {
    const demande = !!(statut && /^Tap anywhere/.test(statut.textContent || ''));
    body.classList.toggle('connexion-geste', demande);
    ecrire(demande ? t[2] : body.classList.contains('connexion-son') ? t[1] : t[0]);
  }
  function son() { body.classList.add('connexion-son'); geste(); }
  function finir() {
    if (fini) return;
    fini = true;
    body.classList.add('connexion-fin');
    setTimeout(function () { body.classList.remove('connexion-on', 'connexion-fin', 'connexion-son', 'connexion-geste'); }, 950);
    obsCorps.disconnect(); obsStatut.disconnect();
  }
  ecrire(t[0]);
  if (audio) {
    audio.addEventListener('playing', son);
    if (!audio.paused && audio.readyState > 2) son();
  }
  const obsStatut = new MutationObserver(geste);
  if (statut) obsStatut.observe(statut, { childList: true, characterData: true, subtree: true });
  const obsCorps = new MutationObserver(function () {
    if (!body.classList.contains('overlay-attente') || body.classList.contains('external-radio')) finir();
  });
  obsCorps.observe(body, { attributes: true, attributeFilter: ['class'] });
  if (!body.classList.contains('overlay-attente')) finir();
  setTimeout(finir, 30000);
})();
